import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import {
  // Apr 30 2026 (App_Change_List #8) — Trophy + Star dropped from
  // import after Quick Actions removal; they only appeared in the
  // action-button list. May 2 2026 — Trophy re-added: now needed
  // for the "Live Scoreboard →" link button that replaced the
  // embedded ScoreboardCard in the today-card.
  Calendar, Users, Plus, ChevronRight, Zap, MapPin, Trophy,
  CheckCircle, XCircle, HelpCircle, ArrowRight, ArrowLeft, ChevronLeft, AlertTriangle,
  Mail, Bell, Clock, Upload, FileText, Settings, Layers, MessageSquare, BarChart2, CreditCard, Copy,
  // v2.5.122 — Phase 8 League Module. Surfaces league announcements
  // addressed to this team's league as a small banner on the team
  // dashboard.
  Megaphone, X,
  // 2026-10-18 — Press Box standalone teams: Radio badges the type chooser.
  Radio,
} from 'lucide-react';
import { PRESS_BOX, pressBoxVisible } from '../constants/pressBox.js';
import { useAuth } from '../context/AuthContext.jsx';
import api from '../services/api.js';
import { IS_NATIVE } from '../utils/apiBase.js';
import ModalCloseButton from '../components/shared/ModalCloseButton.jsx';
import toast from 'react-hot-toast';
import { useTimeFormat } from '../hooks/useTimeFormat.js';
import { useKeyboardSafeSheet } from '../hooks/useKeyboardSafeSheet.js';
import MessageChannelPicker, { DEFAULT_CHANNELS as DEFAULT_MSG_CHANNELS } from '../components/shared/MessageChannelPicker.jsx';
import { formatDuration } from '../utils/formatDuration.js';
import { computeEndTime } from '../utils/computeEndTime.js';
import { ReviewStep } from './ImportSchedulePage.jsx';
import { format, isToday, isTomorrow, differenceInDays, parseISO, isPast, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, addMonths, subMonths } from 'date-fns';
import { getHolidayMap } from '../utils/holidays.js';
import { dedupBySession } from '../utils/sessionDedup.js';

function MiniCalendar({ games, onGameClick, country, onCopyIcal, icalBusy, detailed = false, drinksMap = {}, formatTime, teamDefaultDuration = null }) {
  const [viewDate, setViewDate] = useState(new Date());

  // v2.5.190 — detailed mode renders tall Schedule-style cells (status
  // colour, time range, arena, drinks, +N more) to match the Schedule
  // page month grid. Compact mode (the original) is kept as a fallback.
  const CAL_STYLES = {
    upcoming:    { bg: 'rgba(91,155,213,0.18)',  accent: '#5b9bd5',  label: 'Upcoming' },
    roster_open: { bg: 'rgba(0,188,235,0.18)',   accent: '#00bce0',  label: 'Players Open' },
    locked:      { bg: 'rgba(255,149,0,0.18)',   accent: '#ff9500',  label: 'Locked' },
    completed:   { bg: 'rgba(0,200,100,0.18)',   accent: '#00c864',  label: 'Completed' },
    cancelled:   { bg: 'rgba(255,68,68,0.18)',   accent: '#ff4444',  label: 'Cancelled' },
  };
  const fmtTime = (t) => (formatTime ? formatTime(t) : (t ? t.slice(0, 5) : ''));

  const monthStart  = startOfMonth(viewDate);
  const monthEnd    = endOfMonth(viewDate);
  const calStart    = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calEnd      = endOfWeek(monthEnd,     { weekStartsOn: 0 });
  const days        = eachDayOfInterval({ start: calStart, end: calEnd });

  // Map "YYYY-MM-DD" → game object for fast lookup
  const gameMap = {};
  games.forEach(g => {
    if (!g.game_date) return;
    const key = g.game_date.slice(0, 10);
    if (!gameMap[key]) gameMap[key] = [];
    gameMap[key].push(g);
  });

  // May 1 2026 — statutory holidays. Looked up against the team's
  // country setting (settings.country, ISO-3166-1 alpha-2). Static
  // dataset from utils/holidays.js — no network round-trip per
  // render. Returns a Map<date-string, name>; getHolidayMap returns
  // an empty Map for unsupported countries so the calendar simply
  // shows no holiday markers in that case (legacy behaviour).
  const holidayMap = getHolidayMap(country);
  const hasHolidays = holidayMap.size > 0;

  const cellBase = {
    width: '100%', aspectRatio: '1', borderRadius: 8, display: 'flex',
    alignItems: 'center', justifyContent: 'center', fontSize: 12,
    fontWeight: 500, position: 'relative', cursor: 'default',
    transition: 'all 0.15s ease',
    // Apr 2026 — cap height so iOS accessibility font scaling (or
    // user zoom) can't blow individual cells past their container.
    // Without this, Dynamic Type at large sizes pushed each cell to
    // 100px+ tall, burying the later weeks of the month below the
    // visible scroll area. Capped at 48px: enough for 14pt numbers
    // with the game dot underneath, tight enough that all 5-6 rows
    // fit in a comfortable viewport slice.
    maxHeight: 48,
  };

  // Colours
  const UPCOMING_BG   = 'rgba(0,255,136,0.18)';
  const UPCOMING_FG   = 'var(--goal)';
  const UPCOMING_BDR  = 'rgba(0,255,136,0.4)';
  const TODAY_BG      = 'var(--goal)';
  const TODAY_FG      = '#000';
  const PAST_BG       = 'rgba(167,139,250,0.18)'; // soft purple
  const PAST_FG       = '#a78bfa';
  const PAST_BDR      = 'rgba(167,139,250,0.4)';

  return (
    <div className="zen-card p-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h2 className="section-title text-base" style={{ marginBottom: 0 }}>
          {format(viewDate, 'MMMM yyyy').toUpperCase()}
        </h2>
        <div className="flex gap-1 items-center">
          {/* v2.5.186 — iCal subscribe button beside the calendar.
              Copies the user's feed link to the clipboard (creating it
              on first use), same as the Schedule page button. */}
          {onCopyIcal && (
            <button
              onClick={onCopyIcal}
              disabled={icalBusy}
              title="Subscribe in your calendar app"
              aria-label="Subscribe in your calendar app"
              className="flex items-center gap-1 rounded-lg transition-colors"
              style={{
                background: 'var(--ice-a10, rgba(0,212,255,0.10))',
                border: '1px solid var(--ice-a30, rgba(0,212,255,0.30))',
                color: 'var(--ice)', padding: '4px 8px', fontSize: 11, fontWeight: 700,
                cursor: icalBusy ? 'default' : 'pointer', opacity: icalBusy ? 0.6 : 1,
                marginRight: 4,
              }}>
              <Calendar size={12} />
              {icalBusy ? '…' : 'iCal'}
            </button>
          )}
          <button
            onClick={() => setViewDate(d => subMonths(d, 1))}
            className="w-7 h-7 flex items-center justify-center rounded-lg transition-colors hover:bg-white/10"
            style={{ color: 'var(--text-secondary)' }}>
            <ChevronLeft size={14} />
          </button>
          <button
            onClick={() => setViewDate(d => addMonths(d, 1))}
            className="w-7 h-7 flex items-center justify-center rounded-lg transition-colors hover:bg-white/10"
            style={{ color: 'var(--text-secondary)' }}>
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* Day headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
        {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
          <div key={d} style={{
            textAlign: 'center', fontSize: 10, fontWeight: 700,
            color: 'var(--text-secondary)', letterSpacing: '0.05em',
            padding: '2px 0', fontFamily: 'var(--font-display)',
          }}>{d}</div>
        ))}
      </div>

      {/* Day cells */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: detailed ? 4 : 2 }}>
        {days.map(day => {
          const key      = format(day, 'yyyy-MM-dd');
          const dayGames = gameMap[key] || [];
          const hasGame  = dayGames.length > 0;
          const inMonth  = isSameMonth(day, viewDate);
          const todayDay = isToday(day);
          const pastDay  = !todayDay && isPast(new Date(key + 'T23:59:59'));
          const game     = dayGames[0];
          // May 1 2026 — statutory holiday lookup. holidayMap is keyed
          // by 'YYYY-MM-DD' and returns the holiday name (or
          // undefined). When set, the day gets a soft red tint + the
          // holiday name as a tooltip. Stacks with game styling so a
          // game scheduled on a holiday still gets its own colour
          // ring; the holiday is signaled via tooltip + a small
          // marker bar in the corner.
          const holidayName = holidayMap.get(key);

          const handleClick = () => {
            if (hasGame && game) onGameClick(game.id);
          };

          // v2.5.190 — DETAILED cell (matches Schedule page month grid).
          if (detailed) {
            // Effective status for colour: cancelled wins; a past-dated
            // game OR one with a recorded score reads as completed; else
            // the stored status (default upcoming). Mirrors SchedulePage.
            const effStatus = (() => {
              if (!hasGame) return null;
              if (game.status === 'cancelled') return 'cancelled';
              const hasScore = game.result_home_score != null && game.result_away_score != null;
              if (game.status === 'completed' || hasScore || pastDay) return 'completed';
              return game.status || 'upcoming';
            })();
            const cs = hasGame ? (CAL_STYLES[effStatus] || CAL_STYLES.upcoming) : null;
            const d = hasGame ? drinksMap[game.id] : null;
            const endTime = hasGame ? computeEndTime(game.game_time, game.duration_minutes || teamDefaultDuration) : null;
            return (
              <div
                key={key}
                onClick={handleClick}
                title={holidayName ? `📅 ${holidayName}` : undefined}
                style={{
                  borderRadius: 10, minHeight: 92, padding: '6px 6px 6px',
                  cursor: hasGame ? 'pointer' : 'default',
                  opacity: inMonth ? 1 : 0.3,
                  transition: 'transform 0.12s, box-shadow 0.12s',
                  background: hasGame ? cs.bg
                    : todayDay ? 'rgba(0,188,235,0.10)'
                    : holidayName ? 'rgba(255,68,68,0.06)'
                    : inMonth ? 'var(--surface-2)' : 'transparent',
                  border: hasGame ? `1px solid ${cs.accent}40`
                    : todayDay ? '1px solid rgba(0,188,235,0.40)'
                    : holidayName ? '1px solid rgba(255,68,68,0.18)'
                    : '1px solid var(--border)',
                  borderLeft: hasGame ? `4px solid ${cs.accent}` : undefined,
                  boxShadow: hasGame ? `0 1px 6px ${cs.accent}20` : 'none',
                }}
                onMouseEnter={e => { if (hasGame) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = `0 4px 12px ${cs.accent}35`; } }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = hasGame ? `0 1px 6px ${cs.accent}20` : 'none'; }}
              >
                {/* Day number (+ holiday corner marker) */}
                <div style={{
                  fontSize: 12, fontWeight: hasGame || todayDay ? 800 : 500,
                  color: hasGame ? cs.accent : todayDay ? '#00bce0' : holidayName ? '#dc6868' : 'var(--text-secondary)',
                  fontFamily: 'var(--font-display)', lineHeight: 1,
                  marginBottom: hasGame ? 6 : 0,
                  display: 'flex', alignItems: 'center', gap: 4,
                  textDecoration: (pastDay && hasGame) ? 'line-through' : 'none',
                  opacity: (pastDay && hasGame) ? 0.6 : 1,
                  position: 'relative',
                }}>
                  {todayDay ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: 10, background: hasGame ? cs.accent : '#00bce0', color: '#fff', fontSize: 11, fontWeight: 800 }}>
                      {format(day, 'd')}
                    </span>
                  ) : format(day, 'd')}
                  {holidayName && (
                    <span style={{ position: 'absolute', top: 0, right: 0, width: 6, height: 6, borderRadius: '50%', background: '#dc6868', boxShadow: '0 0 0 1.5px rgba(220,104,104,0.25)' }} />
                  )}
                </div>
                {hasGame && (
                  <div>
                    <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: cs.accent, fontFamily: 'var(--font-display)', marginBottom: 3, opacity: 0.85 }}>
                      {cs.label}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginBottom: 2 }}>
                      <Clock size={9} style={{ color: cs.accent, flexShrink: 0 }} />
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)' }}>
                        {fmtTime(game.game_time)}
                        {endTime && <span style={{ fontWeight: 500, opacity: 0.7 }}>{' – '}{fmtTime(endTime)}</span>}
                      </span>
                    </div>
                    {game.arena_name && (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 3, marginBottom: 2 }}>
                        <MapPin size={8} style={{ color: cs.accent, flexShrink: 0, marginTop: 2 }} />
                        <span style={{ fontSize: 9, color: 'var(--text-secondary)', lineHeight: 1.3, wordBreak: 'break-word' }}>
                          {game.arena_name}
                        </span>
                      </div>
                    )}
                    {d && d.status !== 'ignored' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 8, marginTop: 2, color: d.name ? '#00e87a' : '#ff3b3b' }}>
                        <span>🥤</span>
                        <span style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 60 }}>
                          {d.name ? d.name.split(' ')[0] : 'No drinks!'}
                        </span>
                      </div>
                    )}
                    {dayGames.length > 1 && (
                      <div style={{ display: 'inline-flex', alignItems: 'center', fontSize: 8, fontWeight: 700, letterSpacing: '0.05em', color: '#fff', background: cs.accent, borderRadius: 4, padding: '1px 4px', marginTop: 2, fontFamily: 'var(--font-display)' }}>
                        +{dayGames.length - 1} more
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          }

          let bg     = 'transparent';
          let color  = inMonth ? 'var(--text)' : 'var(--text-secondary)';
          let border = 'none';
          let dotColor = null;

          if (todayDay && !hasGame) {
            bg     = 'rgba(0,212,255,0.12)';
            color  = 'var(--ice)';
            border = '1px solid rgba(0,212,255,0.3)';
          }
          if (hasGame) {
            if (todayDay) {
              bg = TODAY_BG; color = TODAY_FG; border = `1px solid ${TODAY_BG}`; dotColor = '#000';
            } else if (pastDay) {
              bg = PAST_BG; color = PAST_FG; border = `1px solid ${PAST_BDR}`; dotColor = PAST_FG;
            } else {
              bg = UPCOMING_BG; color = UPCOMING_FG; border = `1px solid ${UPCOMING_BDR}`; dotColor = UPCOMING_FG;
            }
          }
          // Holiday tint — only applied when no game is on the date
          // (game styling wins so coaches still see "this is a past
          // / upcoming game" at a glance). When a game IS on a
          // holiday, the small corner marker (rendered below) is
          // the only signal.
          if (holidayName && !hasGame && !todayDay) {
            bg = 'rgba(255,68,68,0.06)';
            color = inMonth ? '#dc6868' : 'var(--text-secondary)';
            border = '1px solid rgba(255,68,68,0.18)';
          }

          // May 1 2026 — strikethrough for past dates with a game.
          // User feedback: the soft purple tint alone wasn't a
          // strong enough "this is history" signal. A diagonal line
          // through the date number reads as unmistakably done.
          // Applied via CSS text-decoration on past+game cells only —
          // empty past cells don't get it (looks weird on blank
          // squares) and today/upcoming cells stay unmarked.
          const strikePast = pastDay && hasGame;

          // Build tooltip — game and/or holiday info.
          const tipParts = [];
          if (hasGame) tipParts.push(dayGames.length > 1 ? `${dayGames.length} games — click to view` : 'Game — click to view');
          if (holidayName) tipParts.push(`📅 ${holidayName}`);
          const tip = tipParts.length ? tipParts.join(' · ') : undefined;

          return (
            <button
              key={key}
              onClick={handleClick}
              disabled={!hasGame}
              title={tip}
              style={{
                ...cellBase,
                background: bg,
                color,
                border,
                opacity: !inMonth ? 0.35 : 1,
                cursor: hasGame ? 'pointer' : 'default',
                fontWeight: todayDay || hasGame ? 700 : 500,
                fontFamily: todayDay || hasGame ? 'var(--font-display)' : 'inherit',
                // May 1 2026 — strikethrough on past games. Combined
                // with the soft purple tint this makes "historical"
                // unmistakable. We use CSS textDecoration rather than
                // a separate overlay element because text-decoration
                // automatically scales with the digit and stays
                // crisp on hi-DPI screens.
                textDecoration: strikePast ? 'line-through' : undefined,
                textDecorationThickness: strikePast ? '2px' : undefined,
                textDecorationColor: strikePast ? 'rgba(167,139,250,0.7)' : undefined,
              }}
              onMouseEnter={e => { if (hasGame) e.currentTarget.style.transform = 'scale(1.15)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
            >
              {format(day, 'd')}
              {hasGame && dotColor && (
                <span style={{
                  position: 'absolute', bottom: 2, left: '50%',
                  transform: 'translateX(-50%)',
                  width: 4, height: 4, borderRadius: '50%',
                  background: dotColor,
                }} />
              )}
              {/* Holiday corner marker — small red bar in the top-
                  right corner. Visible on every holiday cell,
                  including those that ALSO have a game (where the
                  game styling otherwise dominates). The tooltip
                  carries the holiday name. */}
              {holidayName && (
                <span style={{
                  position: 'absolute', top: 2, right: 2,
                  width: 6, height: 6, borderRadius: '50%',
                  background: '#dc6868',
                  boxShadow: '0 0 0 1.5px rgba(220,104,104,0.25)',
                }} />
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 mt-2 pt-2 flex-wrap" style={{ borderTop: '1px solid var(--border)' }}>
        {detailed ? (
          <>
            {Object.entries(CAL_STYLES).map(([k, cs]) => (
              <div key={k} className="flex items-center gap-1.5">
                <div style={{ width: 9, height: 9, borderRadius: 3, background: cs.bg, border: `2px solid ${cs.accent}` }} />
                <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}>{cs.label}</span>
              </div>
            ))}
            {hasHolidays && (
              <div className="flex items-center gap-1.5">
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#dc6868' }} />
                <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}>Holiday</span>
              </div>
            )}
          </>
        ) : (
          <>
        <div className="flex items-center gap-1.5">
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: UPCOMING_FG }} />
          <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}>Upcoming</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: PAST_FG }} />
          <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}>Past</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div style={{ width: 8, height: 8, borderRadius: 3, background: 'rgba(0,212,255,0.3)', border: '1px solid rgba(0,212,255,0.5)' }} />
          <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}>Today</span>
        </div>
        {/* Holiday legend — shown only when the team's country has
            holiday data so we don't dangle a confusing entry for
            countries we don't cover. */}
        {hasHolidays && (
          <div className="flex items-center gap-1.5">
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#dc6868' }} />
            <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}>Holiday</span>
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
}

const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

// ── UpcomingRemindersCard (Apr 2026) ──
// Dashboard widget showing the next few automated team actions
// (auto-nudge, spare auto-invite, auto-balance, roster lock, game-
// day reminder, drinks reminders). Populated from
// GET /teams/:id/scheduled-reminders. Renders nothing when given an
// empty list so the parent can simply mount/unmount based on length.
//
// COLLAPSE BEHAVIOUR (Apr 2026 enhancement):
// On mobile (< 768px) the card defaults to showing just the next
// upcoming reminder so it occupies one row. On desktop it shows the
// next two. Either way, a "Show N more" button reveals the rest;
// tapping again collapses back. The user can override the default
// with the `defaultExpanded` prop (e.g., for the GamePage's per-
// game view where context already implies "show me everything for
// this game"). The hideCollapse prop disables the toggle entirely
// for callers that always want the full list.
//
// Exported so the GamePage can re-use it with a single-game filter
// applied server-side.
export function UpcomingRemindersCard({
  reminders, onGameClick,
  compact = false, title = '🔔 Upcoming Reminders',
  defaultExpanded = false, hideCollapse = false,
}) {
  // Track viewport width so we can pick a sensible default visible
  // count (1 on phones, 2 on desktop). Re-runs on resize so a window-
  // resize from desktop to narrow doesn't leave too many rows visible.
  // SSR-safe initial value falls back to "desktop" since the bigger
  // count is always safe to render and just folds to fewer when the
  // resize handler corrects on first paint.
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 768);
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // How many to show when collapsed. Mobile: 1 (the next one).
  // Desktop: 2 (next + the one after, so the coach can see what's
  // coming). hideCollapse forces the full list regardless.
  const collapsedCount = isNarrow ? 1 : 2;
  const [expanded, setExpanded] = useState(defaultExpanded);
  const showAll = hideCollapse || expanded || reminders.length <= collapsedCount;
  const visible = showAll ? reminders : reminders.slice(0, collapsedCount);
  const hiddenCount = reminders.length - visible.length;

  // Icon + colour accent for each event type. Kept neutral — the
  // card's job is to inform, not alarm. Auto-balance gets a goal-
  // green accent since it's the most "active/exciting" event; nudges
  // use the softer ice-blue.
  const TYPE_STYLE = {
    auto_nudge:          { emoji: '📣', color: 'var(--ice)'   },
    spare_auto_invite:   { emoji: '🏒', color: 'var(--puck)'  },
    auto_balance:        { emoji: '⚖️', color: 'var(--goal)'  },
    roster_lock:         { emoji: '🔒', color: 'var(--text-2)' },
    game_day_reminder:   { emoji: '⏰', color: 'var(--ice)'   },
    drinks_day_before:   { emoji: '🥤', color: 'var(--goal)'  },
    drinks_hours_before: { emoji: '🥤', color: 'var(--puck)'  },
    // v2.5.120 — pre-game reminders. The backend has always
    // documented these types but the preview endpoint wasn't
    // emitting them; both types are now active.
    reminder_1:          { emoji: '📣', color: 'var(--ice)'   },
    reminder_2:          { emoji: '📣', color: 'var(--puck)'  },
  };

  return (
    <div className="zen-card mb-4" style={{ padding: compact ? '12px 14px' : '14px 16px' }}>
      {/* Header — clickable on its own to toggle expand/collapse so
          tapping anywhere on the title row works as a discoverability
          improvement. The button on the right is a more obvious
          affordance for users who don't think to tap the title. */}
      <div
        onClick={() => { if (!hideCollapse && reminders.length > collapsedCount) setExpanded(e => !e); }}
        className="flex items-center gap-2 mb-2"
        style={{
          cursor: !hideCollapse && reminders.length > collapsedCount ? 'pointer' : 'default',
          userSelect: 'none',
        }}>
        <Bell size={14} style={{ color: 'var(--ice)', flexShrink: 0 }} />
        <h3 style={{
          fontFamily: 'var(--font-display)', fontWeight: 800,
          fontSize: compact ? 12 : 13, letterSpacing: '0.05em',
          textTransform: 'uppercase', color: 'var(--text)', margin: 0,
        }}>
          {title}
        </h3>
        <span style={{ fontSize: 10, color: 'var(--text-3)' }}>
          ({reminders.length})
        </span>
        {/* Spacer + chevron toggle. Only render when there's actually
            something to expand/collapse — saves visual noise when
            there's just one or two reminders. */}
        {!hideCollapse && reminders.length > collapsedCount && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setExpanded(p => !p); }}
            aria-label={expanded ? 'Collapse reminders list' : 'Expand reminders list'}
            style={{
              marginLeft: 'auto', background: 'transparent', border: 'none',
              padding: '4px 6px', borderRadius: 6, cursor: 'pointer',
              color: 'var(--ice)', fontSize: 11, fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 3,
            }}>
            {expanded ? 'Show less' : `Show ${hiddenCount} more`}
            <ChevronRight
              size={12}
              style={{
                transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 0.18s ease',
              }}
            />
          </button>
        )}
      </div>

      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {visible.map((r, i) => {
          const style = TYPE_STYLE[r.type] || { emoji: '🔔', color: 'var(--text-2)' };
          // Compose the game-reference line. "Game on Thursday, May 1
          // at 8 PM" so the coach can correlate which game this event
          // belongs to without clicking through.
          let gameRef = null;
          if (r.gameDate) {
            try {
              const d = new Date(`${r.gameDate}T${r.gameTime || '12:00:00'}`);
              if (!isNaN(d.getTime())) {
                const weekday = d.toLocaleDateString('en-US', { weekday: 'short' });
                const md      = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                const hour    = d.getHours();
                const hourL   = hour === 0 ? '12 AM'
                              : hour === 12 ? '12 PM'
                              : hour < 12 ? `${hour} AM`
                              : `${hour - 12} PM`;
                gameRef = `Game: ${weekday} ${md}, ${hourL}`;
              }
            } catch {}
          }

          return (
            <li
              key={`${r.gameId}-${r.type}-${i}`}
              onClick={() => onGameClick?.(r.gameId)}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '9px 10px', borderRadius: 9,
                // Apr 30 2026 (App_Change_List #13) — fired events
                // (auto-jab that already dispatched, etc.) get a
                // dimmed background + subtle styling so coaches can
                // see history alongside pending events without the
                // visual weight pulling attention away from
                // upcoming events.
                background: r.alreadyFired ? 'var(--surface)' : 'var(--surface-2)',
                border: '1px solid var(--border)',
                opacity: r.alreadyFired ? 0.72 : 1,
                cursor: onGameClick ? 'pointer' : 'default',
                transition: 'background 0.12s',
              }}
              onMouseEnter={e => { if (onGameClick) e.currentTarget.style.background = 'var(--surface-3, var(--surface-2))'; }}
              onMouseLeave={e => { if (onGameClick) e.currentTarget.style.background = r.alreadyFired ? 'var(--surface)' : 'var(--surface-2)'; }}>
              <span style={{
                fontSize: 18, lineHeight: 1, marginTop: 1,
                flexShrink: 0,
              }}>
                {style.emoji}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 13, fontWeight: 700, color: 'var(--text)',
                  lineHeight: 1.3, marginBottom: 2,
                  display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
                }}>
                  {r.label}
                  {/* "✓ Sent" pill — App_Change_List #13. Indicates
                      the event has already fired; companion to the
                      dimmed background so the row reads as
                      historical-but-recorded. */}
                  {r.alreadyFired && (
                    <span style={{
                      fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
                      textTransform: 'uppercase', padding: '1px 6px',
                      borderRadius: 4, background: 'rgba(0,232,122,0.12)',
                      color: 'var(--goal)',
                      border: '1px solid rgba(0,232,122,0.30)',
                    }}>
                      ✓ Sent
                    </span>
                  )}
                </div>
                <div style={{
                  fontSize: 11, color: style.color,
                  lineHeight: 1.35, marginBottom: r.detail || gameRef ? 2 : 0,
                }}>
                  <Clock size={10} style={{ display: 'inline', verticalAlign: '-1px', marginRight: 3 }} />
                  {r.fireAtHuman || 'Soon'}
                </div>
                {(r.detail || gameRef) && (
                  <div style={{
                    fontSize: 10, color: 'var(--text-3)', lineHeight: 1.35,
                  }}>
                    {r.detail}{r.detail && gameRef ? ' · ' : ''}{gameRef}
                  </div>
                )}
              </div>
              {onGameClick && (
                <ChevronRight size={14} style={{ color: 'var(--text-3)', flexShrink: 0, marginTop: 4 }} />
              )}
            </li>
          );
        })}
      </ul>

      {/* Collapsed-state hint — when we're hiding rows, give the user
          a second affordance below the list (in case they missed the
          chevron in the header). Keeps it discoverable on small
          screens where the header gets squeezed. */}
      {!hideCollapse && !expanded && hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          style={{
            marginTop: 8, width: '100%',
            padding: '8px 10px', borderRadius: 8,
            background: 'transparent',
            border: '1px dashed var(--border)',
            color: 'var(--text-2)', fontSize: 11, fontWeight: 600,
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            transition: 'background 0.12s, border-color 0.12s, color 0.12s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = 'var(--surface-2)';
            e.currentTarget.style.borderColor = 'var(--ice-a30, var(--border))';
            e.currentTarget.style.color = 'var(--ice)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = 'var(--border)';
            e.currentTarget.style.color = 'var(--text-2)';
          }}>
          <ChevronRight size={11} style={{ transform: 'rotate(90deg)' }} />
          Show {hiddenCount} more reminder{hiddenCount === 1 ? '' : 's'}
        </button>
      )}

      {!compact && showAll && reminders.length > 0 && (
        <p style={{
          fontSize: 10, color: 'var(--text-3)', marginTop: 8, lineHeight: 1.4,
          fontStyle: 'italic',
        }}>
          Times are approximate — cron runs every 15-30 minutes so actual send may be slightly after.
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// LeagueAnnouncementsBanner — v2.5.122 (Phase 8 surface)
// ─────────────────────────────────────────────────────────────────
// Renders a compact, dismissible banner of unread league
// announcements for the active team's league. Quietly renders
// nothing if:
//   • The team isn't enrolled in a league (the endpoint returns
//     an empty list with league_id: null).
//   • All announcements have been dismissed by the current user.
//
// Dismissals are per-user and server-recorded — clicking the X
// hides the announcement for THIS user only; other team members
// still see it. Retracting (only available to league managers
// from the league side) hides it for everyone.
//
// Severity colors match LeagueAnnouncementsPage (urgent = red,
// important = cyan, info = neutral). Pinned/urgent items render
// at the top.
//
// Click-through navigates to the league announcements page so
// the user can read the full body when there's one.
function LeagueAnnouncementsBanner({ teamId }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;
    api.get(`/teams/${teamId}/league-announcements?limit=10`)
      .then(({ data }) => {
        if (cancelled) return;
        // Filter to un-dismissed only. The dashboard banner is a
        // pure unread-feed; the full LeagueAnnouncementsPage shows
        // dismissed history.
        const unread = (data?.announcements || []).filter(a => !a.is_dismissed);
        setItems(unread);
      })
      .catch(() => { /* swallow — silent failure is fine on dashboard */ })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [teamId]);

  async function dismiss(id) {
    // Optimistic — remove from local state first; only roll back if
    // the API call fails badly. (Dismissal is idempotent, so even
    // network blips usually succeed on retry.)
    setItems(prev => prev.filter(a => a.id !== id));
    try {
      await api.post(`/teams/${teamId}/league-announcements/${id}/dismiss`);
    } catch {
      // Silent. Next page load will refetch and resurface if it
      // somehow failed server-side.
    }
  }

  if (!loaded || items.length === 0) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      {items.map(a => {
        const sev = sevColorsForBanner(a.severity);
        return (
          <div key={a.id} style={{
            display: 'flex', gap: 12, alignItems: 'flex-start',
            background: sev.bg,
            border: `1px solid ${sev.border}`,
            borderLeft: `4px solid ${sev.accent}`,
            borderRadius: 8,
            padding: '10px 14px',
            marginBottom: 8,
          }}>
            <Megaphone size={18} style={{ color: sev.accent, flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 11, color: sev.label, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.08em',
                marginBottom: 2,
              }}>
                {a.severity === 'urgent' ? '🚨 Urgent' : a.severity === 'important' ? 'Important' : 'League notice'}
                {a.is_pinned && ' · Pinned'}
              </div>
              <div style={{ fontWeight: 700, color: 'var(--text)', fontSize: 14 }}>
                {a.title}
              </div>
              {a.body && (
                <div style={{
                  fontSize: 13, color: 'var(--text-2)', marginTop: 4,
                  // Truncate long bodies on the dashboard — full text
                  // shows on click-through to the announcements page.
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}>
                  {a.body}
                </div>
              )}
              <button
                onClick={() => navigate(`/league/${a.league_id}/announcements`)}
                style={{
                  marginTop: 6,
                  background: 'none', border: 'none', padding: 0,
                  color: sev.accent, fontWeight: 700, fontSize: 12,
                  cursor: 'pointer', textDecoration: 'none',
                }}>
                View all →
              </button>
            </div>
            <button
              onClick={() => dismiss(a.id)}
              aria-label="Dismiss"
              title="Mark as read"
              style={{
                background: 'none', border: 'none', padding: 4,
                color: 'var(--text-2)', cursor: 'pointer',
                flexShrink: 0,
              }}>
              <X size={16} />
            </button>
          </div>
        );
      })}
    </div>
  );
}

function sevColorsForBanner(s) {
  if (s === 'urgent')    return { bg: 'rgba(255,80,80,0.08)',  border: 'rgba(255,80,80,0.25)',  accent: '#ff5050', label: '#a8000a' };
  if (s === 'important') return { bg: 'rgba(0,184,224,0.08)',  border: 'rgba(0,184,224,0.25)',  accent: '#00b8e0', label: '#005a78' };
  return                       { bg: 'rgba(122,130,160,0.08)', border: 'rgba(122,130,160,0.20)', accent: '#7a82a0', label: '#4a526e' };
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { teams, teamsLoaded, activeTeam, setActiveTeam, loadTeams } = useOutletContext();

  // 2026-10-18 — a standalone Press Box team has no dashboard: its home is
  // the Press Box games list. Jump there unless the user is opening the
  // new-team wizard (which lives on this page).
  const pbTeamId = activeTeam?.team_type === 'press_box' ? activeTeam.id : null;
  useEffect(() => {
    const openingWizard = new URLSearchParams(window.location.search).get('new') === '1';
    if (pbTeamId && !openingWizard) navigate(`/press-box/${pbTeamId}`, { replace: true });
  }, [pbTeamId]);   // eslint-disable-line react-hooks/exhaustive-deps
  const navigate = useNavigate();

  // v2.5.186 — "iCal" copy for the dashboard calendar, mirroring the
  // Schedule page button: find/create this user's calendar feed token
  // for the active team and copy the https ICS URL to the clipboard.
  const [dashIcalBusy, setDashIcalBusy] = useState(false);
  const copyDashboardIcal = useCallback(async () => {
    if (dashIcalBusy || !activeTeam?.id) return;
    setDashIcalBusy(true);
    try {
      let token = null;
      try {
        const { data } = await api.get('/me/calendar-tokens');
        const list = Array.isArray(data?.tokens) ? data.tokens : (Array.isArray(data) ? data : []);
        token = list.find(t => t.teamId === activeTeam.id)?.token || null;
      } catch { /* fall through to create */ }
      if (!token) {
        const { data } = await api.post('/me/calendar-tokens', { teamId: activeTeam.id });
        token = data?.token || null;
      }
      if (!token) { toast.error('Could not create your calendar link'); return; }
      const host = (IS_NATIVE || (typeof window !== 'undefined' && window.location?.hostname === 'app.game-day.app'))
        ? 'api.game-day.app'
        : (typeof window !== 'undefined' ? window.location.host : 'api.game-day.app');
      const url = `https://${host}/api/calendar/${token}.ics`;
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        const input = document.createElement('input');
        input.value = url; document.body.appendChild(input); input.select();
        document.execCommand('copy'); document.body.removeChild(input);
      }
      toast.success('Calendar link copied — paste it into your calendar app to subscribe.', { duration: 5000 });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not copy calendar link');
    } finally {
      setDashIcalBusy(false);
    }
  }, [dashIcalBusy, activeTeam]);

  const [upcomingGames, setUpcomingGames] = useState([]);
  const [recentGames,   setRecentGames]   = useState([]);
  const [allGames,      setAllGames]      = useState([]);
  // v2.5.190 — month-grid detail data. drinksMap is keyed by game id
  // (same shape the Schedule grid uses) so the dashboard calendar can
  // show the drinks volunteer per game; teamDefaultDuration feeds the
  // start–end time range when a game has no explicit duration.
  const [calDrinksMap,  setCalDrinksMap]  = useState({});
  const [teamDefaultDuration, setTeamDefaultDuration] = useState(null);
  const [stats,         setStats]         = useState(null);
  // 2026-07-25 — leaderboard rows for the player "My Stats" dashboard
  // card (the user's own row is picked out at render time).
  const [dashLeaderboard, setDashLeaderboard] = useState([]);
  // Apr 2026 — track whether the active team has any seasons. Used to
  // swap the "+ New Team" header CTA to "+ New Season" for fresh
  // teams that don't have a season yet (the natural next step after
  // team creation is creating a season, not creating another team).
  // null = not loaded yet (initial), [] = loaded but empty (no
  // seasons), [...n] = loaded with seasons. Distinguishing null from
  // [] avoids the brief flicker where "+ New Season" shows for a
  // team that actually has seasons before the GET resolves.
  const [seasons,       setSeasons]       = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [rsvping,       setRsvping]       = useState({});
  // Upcoming automated reminders (auto-nudge, spare auto-invite,
  // auto-balance, etc.) — coach-only widget. Populated by a GET to
  // /teams/:id/scheduled-reminders. null = not yet loaded; [] = nothing
  // to show.
  const [scheduledReminders, setScheduledReminders] = useState(null);
  // Dashboard-level "Email Players" composer — same backend endpoint as the
  // Players page, but group-filter only (no per-player picker, which lives on
  // the Players page where the member list is already loaded).
  const [msgOpen,      setMsgOpen]      = useState(false);
  // 2026-09-16 — keeps the message textarea visible above the mobile
  // keyboard (same fix as the GamePage / SchedulePage Modal). Must be
  // called unconditionally here even though the sheet is conditional.
  const { sheetRef: msgSheetRef, vvTop: msgVvTop } = useKeyboardSafeSheet();
  const [msgGroup,     setMsgGroup]     = useState('all');
  const [msgSubject,   setMsgSubject]   = useState('');
  const [msgBody,      setMsgBody]      = useState('');
  const [msgSending,   setMsgSending]   = useState(false);
  // Apr 2026 (Tom_Notes B8) — channel routing for the dashboard
  // Quick Action message blast.
  const [msgChannels,  setMsgChannels]  = useState(DEFAULT_MSG_CHANNELS); // 2026-09-17 — default 'prefs'
  // v2.5.225 — roster-aware upgrade (parity with the game-page Message box):
  // load the team roster when the box opens so we can show a live recipient
  // preview and a per-player picker. Preset groups still send `group`;
  // "Pick Players" sends an explicit userIds list.
  const [msgRoster,        setMsgRoster]        = useState([]);
  const [msgRosterLoading, setMsgRosterLoading] = useState(false);
  const [msgPicked,        setMsgPicked]        = useState(() => new Set()); // userIds (Pick Players)
  const [msgSearch,        setMsgSearch]        = useState('');
  // v2.5.226 — tapping the recipient-preview chip opens a scrollable popup
  // listing every name the message will go to (a quick "who's this hitting?").
  const [msgRecipientsOpen, setMsgRecipientsOpen] = useState(false);

  const { formatTime } = useTimeFormat();

  // Apr 2026 — sidebar "+ New Team" button navigates to
  // /dashboard?new=1 rather than wiring through context. Cleaner,
  // works from any page (the NavLink just has a target URL), and
  // deep-linking to "open the create-team wizard" is a nice side
  // effect. This effect picks up the param, opens the wizard, and
  // cleans the URL so reloads don't re-trigger it.
  const [searchParams, setSearchParams] = useSearchParams();
  const shouldOpenWizard = searchParams.get('new') === '1';

  // Wizard
  const [showWizard,   setShowWizard]   = useState(false);
  // 2026-10-18 — with Press Box on the account, "+ New Team" first asks
  // which kind of team: a full team (this wizard) or a standalone Press Box
  // team (its own setup at /press-box/setup — no settings copied, no
  // payments). null = not chosen yet.
  const [wizardType,   setWizardType]   = useState(null);
  const [wizardStep,   setWizardStep]   = useState(1);
  const [wizardTeam,   setWizardTeam]   = useState(null);
  const [wizardForm,   setWizardForm]   = useState({
    // Apr 2026 — wizard rebuilt per coach feedback. Removed gameDay
    // and gameTime (those come from the imported schedule, not the
    // wizard); removed arenaName/arenaAddress (same reason). Added
    // teamSport so a coach picks once at team creation rather than
    // discovering it later in Team Settings. Field bindings kept
    // around but unused so any older callers don't crash on the
    // wizard form shape.
    name: '', leagueName: '', teamSport: 'hockey',
    // 2026-07-10 — first-season name (wizard step 1, coach request).
    // Backend creates + activates the season and seeds season_members.
    // leagueName above is now legacy too (field removed from the wizard;
    // still editable in Team Settings) — binding kept so nothing crashes.
    seasonName: '',
    gameDay: 'Monday', gameTime: '20:00',  // legacy — not exposed in UI
    arenaName: '', arenaAddress: '',       // legacy — not exposed in UI
  });
  // Apr 2026 — wizard step 3 ("Players") supports copying the player
  // list from one of the coach's other teams. We pre-fetch the
  // coach's existing teams in the dashboard load below; here we just
  // track which one (if any) the coach picked. Empty string = none.
  const [wizardCopyFromTeamId, setWizardCopyFromTeamId] = useState('');
  // 2026-07-10 (wizard v2) — drives the step-4 checklist ✓/✗ for players.
  const [wizardPlayersImported, setWizardPlayersImported] = useState(false);
  // 2026-07-10 (Phase 1.5, coach request) — copy scope: everyone (default),
  // full-timers only, or spares only. Coach reported one path bringing over
  // only full-timers; the scope is now an explicit choice.
  const [wizardCopyScope, setWizardCopyScope] = useState('everyone');
  // 2026-08-07 (new-season incident) — email copied players that they've
  // been added. Default ON: the silent copy left existing users with no
  // idea the new team existed (they only found out from the NO RESPONSE
  // nag, or never). Coach can untick for a quiet copy.
  const [wizardNotifyCopied, setWizardNotifyCopied] = useState(true);
  // 2026-09-17 (per Gilles, Phase 6) — Stripe transfer sub-step, shown
  // right after step 2 (Settings) ONLY when the coach already has a
  // finished Stripe account on another team (GET /stripe-connect/status
  // → `reusable`). null = not checked yet / nothing to offer;
  // { reusable: [...] } = show the screen.
  const [wizardStripe, setWizardStripe] = useState(null);
  const [wizardStripeBusy, setWizardStripeBusy] = useState(false);
  const [wizardCopyingPlayers, setWizardCopyingPlayers] = useState(false);
  // 2026-07-10 (Phase 2.5) — NEW step 2 "Team Settings": start with
  // defaults, or copy the full settings bundle (reminders, balancer,
  // payments, drinks, etc.) from another team the coach runs. 'keep' =
  // fresh defaults (backend already applies sensible defaults on create).
  const [wizardSettingsMode,       setWizardSettingsMode]       = useState('keep');   // keep | copy
  const [wizardSettingsFromTeamId, setWizardSettingsFromTeamId] = useState('');
  const [wizardApplyingSettings,   setWizardApplyingSettings]   = useState(false);
  // Apr 2026 — wizard step 2 ("Schedule") supports a PDF/image upload
  // that runs through the schedule extractor AI. We track the
  // extraction phase here so the UI can show an inline status.
  const [wizardScheduleFile,    setWizardScheduleFile]    = useState(null);
  const [wizardScheduleStatus,  setWizardScheduleStatus]  = useState('idle'); // idle | uploading | extracting | done | error
  const [wizardScheduleResult,  setWizardScheduleResult]  = useState(null);   // { games, gamesCount, provider, filename }
  // Apr 30 2026 — when true, render the inline ReviewStep inside the
  // wizard instead of the "found N games" success card. Bug fix: the
  // old flow navigated to /games/import which started a fresh wizard
  // and lost the extracted games. Now the coach reviews + saves
  // without leaving the wizard.
  const [wizardReviewing,       setWizardReviewing]       = useState(false);
  const [wizardScheduleError,   setWizardScheduleError]   = useState(null);
  const [creating,     setCreating]     = useState(false);
  // Apr 2026 — `savingArena` and `wizardInvite` state removed; both
  // belonged to wizard steps that were replaced (Home Arena → schedule
  // import; one-at-a-time invites → bulk copy-from-team or skip).

  useEffect(() => {
    if (shouldOpenWizard) {
      setShowWizard(true);
      setWizardStep(1);
      setWizardType(null);   // re-ask the kind of team on every fresh open
      // Remove the param so a refresh doesn't re-pop the wizard.
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldOpenWizard]);

  // 2026-07-11 — one-shot 429 retry guard (reset on every fresh load).
  const dashRetriedRef = useRef(false);
  // 2026-07-11 sweep fix — WHY: the comment above always claimed the guard
  // resets "on every fresh load", but nothing ever set it back to false, so
  // it was really a ONE-SHOT FOR THE LIFETIME OF THE COMPONENT: after the
  // first 429 (mount, team switch, or the visibility/focus refetch below)
  // used up the retry, every LATER 429 — including ones on a totally
  // different load, minutes later — skipped the retry and went straight to
  // the "Failed to load dashboard" toast, i.e. the exact rate-limit
  // regression this guard was built to prevent. Fix: the internal one-shot
  // retry (setTimeout below) now calls loadDashboard(teamId, true); every
  // OTHER caller (mount/team-switch effect, visibility/focus effect) omits
  // the flag, so only genuinely-fresh loads get a new retry budget — the
  // recursive retry call itself does not reset its own guard, so a
  // persistent 429 still can't loop forever.
  const loadDashboard = useCallback(async (teamId, isRetryContinuation = false) => {
    if (!isRetryContinuation) dashRetriedRef.current = false;
    setLoading(true);
    try {
      const now = new Date();
      // May 1 2026 — was making TWO /games calls in parallel:
      //   1. /games (full list)
      //   2. /games?status=completed&limit=3
      // The first call already returns every non-cancelled game on
      // the team's active season, so the second call was redundant —
      // we can derive `recentGames` by filtering+sorting the first
      // result client-side. Each /games call hits a heavy SQL with
      // multiple LEFT JOINs and COUNT DISTINCT FILTERs (see
      // backend/src/routes/games.js GET /), so cutting one out
      // shaves 200-800ms off the dashboard load on coach accounts
      // with deep history.
      // May 6 2026 (v2.5.71) — error handlers below distinguish
      // between "endpoint failed for non-auth reasons" (graceful
      // empty fallback so the rest of the dashboard still renders)
      // and "401 from expired token" (let it propagate so the api.js
      // interceptor can refresh + retry, or surface the genuine
      // logout if refresh fails).
      // Without this distinction, the silent fallback to {data:[]}
      // was hiding the auth failure — users saw "No upcoming games"
      // for a brief moment between the bad-auth response and the
      // interceptor-driven redirect to /login. With the new fix
      // in api.js (refresh-token rotation race recovery) the
      // refresh path itself is more robust, but the dashboard
      // load shouldn't pretend "no games" when the truth is "no
      // valid auth."
      // 2026-07-11 (rate-limit incident follow-up) — 429 now RETHROWS
      // too: a rate-limited /games response was silently swallowed to
      // {data:[]}, rendering a convincing-but-false "No upcoming games"
      // (the original "blank dashboard on double refresh" symptom). The
      // catch below retries ONCE after a short backoff instead.
      const swallowExceptAuth = (err) => {
        const s = err?.response?.status;
        if (s === 401 || s === 429) throw err;
        return { data: [] };
      };
      const swallowExceptAuthObj = (err) => {
        const s = err?.response?.status;
        if (s === 401 || s === 429) throw err;
        return { data: {} };
      };
      const [gamesRes, statsRes, seasonsRes, drinksRes, teamRes, lbRes] = await Promise.all([
        api.get(`/teams/${teamId}/games`).catch(swallowExceptAuth),
        api.get(`/teams/${teamId}/stats`).catch(swallowExceptAuthObj),
        // Apr 2026 — fetch seasons so the "+ New Team" header CTA can
        // contextually swap to "+ New Season" when the team has none.
        // Failure-tolerant (non-auth): empty list on error means the
        // button falls back to the safe "New Team" default.
        api.get(`/teams/${teamId}/seasons`).catch(swallowExceptAuth),
        // v2.5.190 — drinks map (keyed by game id) + team (for default
        // game duration) so the dashboard month grid can show the
        // detailed Schedule-style cells. Both failure-tolerant.
        api.get(`/teams/${teamId}/drinks`).catch(() => ({ data: {} })),
        api.get(`/teams/${teamId}`).catch(() => ({ data: null })),
        // 2026-07-25 (per Gilles) — leaderboard fetch feeds the new
        // player-facing "My Stats" card at the top of the dashboard
        // (their own W-L, GF/GA, +/- row). Failure-tolerant: no card
        // on error. Coaches skip the card (they have the Stats page)
        // but the fetch is shared so a coach demoted mid-session
        // doesn't need a reload.
        api.get(`/teams/${teamId}/stats/leaderboard`).catch(() => ({ data: [] })),
      ]);
      setDashLeaderboard(Array.isArray(lbRes.data) ? lbRes.data : []);
      setCalDrinksMap(drinksRes.data || {});
      {
        // v2.5.190 fix — match SchedulePage's source of truth exactly:
        // settings.default_game_duration_minutes. (The earlier
        // default_duration key didn't exist, so the end-time always fell
        // back to 90 min and never matched the team's real setting.)
        const dd = teamRes?.data?.settings?.default_game_duration_minutes;
        if (dd) setTeamDefaultDuration(dd);
      }
      const allGamesData = gamesRes.data || [];
      // May 8 2026 (v2.5.87) — coach feedback: "for some reason it's
      // showing May 10 twice." Two cards on the dashboard for the same
      // date/time/arena, with different IN/OUT counts (19/7 and 19/0).
      // The second card is a MINI-GAME SIBLING — when a coach ends a
      // mini-game session, the parent gets status='completed' and a
      // sibling is spawned with parent_game_id = parent.id and a
      // game_time offset by N seconds (games.js line 5709-5738). The
      // sibling carries over IN players (matching counts) but not OUT
      // (different counts). formatTime renders only hh:mm so the
      // seconds offset is invisible — hence "the same game shown
      // twice." If the user spawns a sibling without completing the
      // parent (or both end up upcoming for any other reason), both
      // appear in the upcoming list.
      //
      // Dedup by SESSION ROOT (parent_game_id || id). One card per
      // session. Pick the LATEST game_time within the session — that
      // matches the coach's mental model ("show me the next thing
      // happening on this slot"), works whether the parent is
      // completed or not, and handles 3+ siblings gracefully.
      // v2.5.93 — dedup logic moved to shared util
      // (frontend/src/utils/sessionDedup.js) so the schedule page
      // can use the same algorithm. Two preference hints introduced:
      // 'upcoming' for the upcoming list (prefer not-completed
      // siblings), 'completed' for the recent list (prefer
      // completed siblings, with real scores).
      const upcoming = dedupBySession(
        allGamesData.filter(g => g.status !== 'cancelled' &&
          new Date(g.game_date.slice(0,10) + 'T12:00:00') >= new Date(now.toDateString())),
        'upcoming')
        .sort((a, b) => a.game_date.localeCompare(b.game_date) || (a.game_time || '').localeCompare(b.game_time || ''))
        .slice(0, 5);
      // Derive recent games from the same fetch — completed games
      // sorted newest-first, take 3. Same dedup applied with the
      // 'completed' hint so we get the meaningful sibling (real
      // score) rather than an empty upcoming companion.
      const recent = dedupBySession(
        allGamesData.filter(g => g.status === 'completed'),
        'completed')
        .sort((a, b) =>
          (b.game_date || '').localeCompare(a.game_date || '') ||
          (b.game_time || '').localeCompare(a.game_time || ''))
        .slice(0, 3);
      setUpcomingGames(upcoming);
      setRecentGames(recent);
      // v2.5.87 — apply the same session dedup to allGames (used by
      // MiniCalendar). Without this, the calendar's "N games — click
      // to view" tooltip would say "2 games" on a date that the
      // upcoming list now shows as a single card, which is confusing.
      // After dedup the calendar dot still renders, the tooltip is
      // accurate, and clicking navigates to the same game card the
      // upcoming list points to. v2.5.93 — uses shared util.
      // Preference 'latest' (default) for the calendar since each
      // day's tooltip should reflect the most-recently-played mini.
      setAllGames(dedupBySession(allGamesData.filter(g => g.status !== 'cancelled')));
      setStats(statsRes.data || {});
      setSeasons(Array.isArray(seasonsRes.data) ? seasonsRes.data : []);
    } catch (err) {
      console.error('Dashboard load error:', err);
      // May 6 2026 (v2.5.71) — suppress the user-facing toast and
      // the empty-state render when the failure is auth-related.
      // The api.js interceptor is in the process of either refreshing
      // the token (transient) or redirecting to /login (terminal);
      // toasting "Failed to load dashboard" in that window confuses
      // the user, and flipping loading=false would briefly render
      // "No upcoming games" before the redirect lands.
      if (err?.response?.status === 429 && !dashRetriedRef.current) {
        // 2026-07-11 — retry ONCE on rate-limit after a short backoff,
        // keeping the skeleton up (loading stays true). A second 429
        // falls through to the error toast instead of faking "no games".
        dashRetriedRef.current = true;
        // isRetryContinuation=true — do NOT let this recursive call reset
        // the guard back to false, or a persistent 429 would retry forever.
        setTimeout(() => loadDashboard(teamId, true), 2000);
        return;
      }
      if (err?.response?.status !== 401) {
        toast.error('Failed to load dashboard — try refreshing');
        setLoading(false);
      }
      // For 401: leave loading=true so the skeleton stays up until
      // the interceptor either refreshes-and-retries (next call to
      // loadDashboard fires from the useEffect) or navigates to /login.
      return;
    }
    setLoading(false);
  }, []);

  // Coach-only scheduled-reminders preview. Separated from the main
  // loadDashboard so it can be re-fetched independently (e.g., after
  // changing automation settings). Fails silently to avoid spamming
  // toasts — the section is purely informational.
  const loadScheduledReminders = useCallback(async (teamId, memberRole) => {
    if (!['coach', 'manager'].includes(memberRole)) {
      setScheduledReminders([]);
      return;
    }
    try {
      // v2.5.120 — limit bumped from 8 → 12. The endpoint started
      // emitting reminder_1 / reminder_2 events (previously missing
      // despite being documented). Each upcoming game can now
      // produce up to ~6 events instead of ~4, so the old cap
      // would have crowded out future-game reminders behind a
      // single near-term game's full list.
      const { data } = await api.get(`/teams/${teamId}/scheduled-reminders?limit=12`);
      setScheduledReminders(data.events || []);
    } catch (err) {
      console.error('scheduled-reminders load failed:', err.message);
      setScheduledReminders([]);
    }
  }, []);

  useEffect(() => {
    if (activeTeam) {
      loadDashboard(activeTeam.id);
      loadScheduledReminders(activeTeam.id, activeTeam.member_role);
    } else {
      setLoading(false);
    }
  }, [activeTeam, loadDashboard, loadScheduledReminders]);

  // May 2 2026 — refresh dashboard data when the page becomes visible
  // again (after the user has been on another page/tab/app). Without
  // this, the today-card RSVP buttons stayed stale: a coach on the
  // waitlist would tap I'M IN on the game page, get added to the
  // waitlist, navigate back to the dashboard, and STILL see the
  // "I'M IN / OUT" buttons because the dashboard fetched once at
  // mount and never re-checked. Now: tab focus / app foreground /
  // visibility-change all re-fetch. Cheap (one /games endpoint hit)
  // and prevents the cross-page state drift.
  //
  // Throttled to once per 3s so rapid tab-switching doesn't hammer
  // the API. Most user-visible cases (returning to the app after a
  // few seconds away) clear the throttle window naturally.
  useEffect(() => {
    if (!activeTeam) return;
    let lastRefresh = Date.now();
    const tryRefresh = () => {
      if (document.hidden) return;
      const now = Date.now();
      if (now - lastRefresh < 3000) return;
      lastRefresh = now;
      loadDashboard(activeTeam.id);
    };
    document.addEventListener('visibilitychange', tryRefresh);
    window.addEventListener('focus', tryRefresh);
    return () => {
      document.removeEventListener('visibilitychange', tryRefresh);
      window.removeEventListener('focus', tryRefresh);
    };
  }, [activeTeam, loadDashboard]);

  const rsvp = async (e, gameId, status) => {
    e.stopPropagation();
    if (rsvping[gameId]) return;
    setRsvping(p => ({ ...p, [gameId]: true }));
    try {
      await api.post(`/teams/${activeTeam.id}/games/${gameId}/availability`, { status });
      setUpcomingGames(prev => prev.map(g => g.id === gameId ? { ...g, my_status: status } : g));
      toast.success({ in:"You're in! 🏟", out:"Marked as out" }[status] || 'Updated');
      // May 2 2026 — suggest-push event no longer dispatched. The
      // push permission nudge banner was removed entirely; users
      // who want push subscribe deliberately from Profile.
    } catch (err) {
      // May 4 2026 — surface the server's actual error message instead
      // of a generic "Failed to update". Important for the lookahead
      // limit (LOOKAHEAD_EXCEEDED, 403) where the message tells the
      // user WHY ("This team only allows RSVPs for the next N games")
      // and the next action ("check back closer to the game date").
      // Falls back to the old generic message if the server didn't
      // send a useful one.
      toast.error(err.response?.data?.error || 'Failed to update availability');
    }
    setRsvping(p => ({ ...p, [gameId]: false }));
  };

  // Open the Email Players modal from Dashboard quick action. Reset draft so
  // re-opens don't carry stale text from a cancelled previous compose.
  const openMessagePlayers = () => {
    setMsgGroup('all');
    setMsgSubject('');
    setMsgBody('');
    setMsgPicked(new Set());
    setMsgSearch('');
    setMsgRecipientsOpen(false);
    setMsgOpen(true);
    // v2.5.225 — load the roster so the box can preview recipients + power
    // the Pick Players picker. Captures activeTeam.id at click time so a
    // later team switch can't show a stale roster. Failure → empty roster
    // (presets still send via `group`, which the backend resolves server-side).
    if (activeTeam) {
      setMsgRoster([]);            // drop any prior team's roster while loading
      setMsgRosterLoading(true);
      api.get(`/teams/${activeTeam.id}/members`)
        .then(({ data }) => setMsgRoster(Array.isArray(data) ? data : []))
        .catch(() => setMsgRoster([]))
        .finally(() => setMsgRosterLoading(false));
    }
  };

  const sendMessagePlayers = async () => {
    if (!activeTeam) return;
    if (!msgSubject.trim()) return toast.error('Subject is required');
    if (!msgBody.trim())    return toast.error('Message body is required');
    if (!Array.isArray(msgChannels) || msgChannels.length === 0) {
      return toast.error('Pick at least one channel (player preferences, email, push, or SMS)');
    }
    // v2.5.225 — "Pick Players" sends an explicit userIds list; preset groups
    // still send `group` (unchanged backend behavior). Guard the picker count
    // client-side (backend also caps at 100) so the coach gets a clear error.
    if (msgGroup === 'specific' && msgPicked.size === 0) {
      return toast.error('Pick at least one player');
    }
    if (msgGroup === 'specific' && msgPicked.size > 100) {
      return toast.error('Maximum 100 recipients per send');
    }
    setMsgSending(true);
    try {
      const payload = {
        subject:  msgSubject.trim(),
        body:     msgBody.trim(),
        // Apr 2026 (Tom_Notes B8) — channels.
        channels: msgChannels,
      };
      if (msgGroup === 'specific') payload.userIds = [...msgPicked];
      else                         payload.group   = msgGroup;
      const { data } = await api.post(`/teams/${activeTeam.id}/messages/send`, payload);
      const bd = data.breakdown || {};
      const parts = [];
      if (bd.emailSent != null && msgChannels.includes('email')) {
        parts.push(`${bd.emailSent} email${bd.emailSent !== 1 ? 's' : ''}`);
      }
      if (bd.pushSent != null && msgChannels.includes('push')) {
        parts.push(`${bd.pushSent} push`);
      }
      if (bd.smsSent != null && bd.smsSent > 0 && (msgChannels.includes('sms') || msgChannels.includes('prefs'))) {
        parts.push(`${bd.smsSent} SMS`);
      }
      if (msgChannels.includes('prefs') && parts.length === 0) {
        if (bd.emailSent) parts.push(`${bd.emailSent} email${bd.emailSent !== 1 ? 's' : ''}`);
        if (bd.pushSent)  parts.push(`${bd.pushSent} push`);
      }
      if (bd.inAppSent != null && msgChannels.includes('in_app')) {
        parts.push(`${bd.inAppSent} in-app`);
      }
      if (data.sent > 0 || parts.length > 0) {
        const summary = parts.length > 0
          ? `Sent: ${parts.join(' · ')}`
          : `Sent to ${data.sent ?? 0} of ${data.total ?? 0} recipient${(data.total ?? 0) !== 1 ? "s" : ""}`;
        toast.success(`📧 ${summary}`);
      } else {
        toast(
          '⚠️ No messages sent — no matching recipients (all opted out, or no email/push subscriptions).',
          { icon: '⚠️', duration: 6000 }
        );
      }
      setMsgOpen(false);
      setMsgSubject('');
      setMsgBody('');
      setMsgChannels(['email']);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send messages');
    }
    setMsgSending(false);
  };

  const wizardCreateTeam = async (e) => {
    e.preventDefault();
    setCreating(true);
    // 2026-07-10 (wizard v2) — EDIT MODE: the Back button can return to
    // step 1 AFTER the team was created (step-1 Next creates it).
    // Re-submitting must UPDATE the existing team + rename its first
    // season — never POST a second team (that was a live duplicate-team
    // footgun whenever Back was used).
    if (wizardTeam) {
      try {
        const newName = (wizardForm.name || '').trim();
        if (newName && newName !== wizardTeam.name) {
          await api.put(`/teams/${wizardTeam.id}`, { name: newName });
        }
        const s = wizardTeam.initialSeason;
        const newSeason = (wizardForm.seasonName || '').trim();
        if (s?.id && newSeason && newSeason !== s.name) {
          await api.patch(`/teams/${wizardTeam.id}/seasons/${s.id}`, { name: newSeason });
        }
        setWizardTeam(t => ({
          ...t,
          name: newName || t.name,
          initialSeason: s ? { ...s, name: newSeason || s.name } : s,
        }));
        setWizardStep(2);
      } catch (err) {
        toast.error(err.response?.data?.error || 'Failed to update team');
      }
      setCreating(false);
      return;
    }
    try {
      // Apr 2026 — wizard simplified. Game day/time are not asked at
      // create time; they get populated from the imported schedule
      // (step 2). Pass the legacy form values through so any team
      // that skips the schedule import still has SOMETHING in those
      // fields (the Team page UI still renders them).
      const { data } = await api.post('/teams', {
        name: wizardForm.name,
        // 2026-07-10 — leagueName no longer collected in the wizard
        // (set it later in Team Settings). First season replaces it:
        // created + activated server-side; undefined = no season.
        initialSeasonName: (wizardForm.seasonName || '').trim() || undefined,
        gameDay: wizardForm.gameDay, gameTime: wizardForm.gameTime,
      });
      setWizardTeam(data);

      // Apr 2026 — sport is stored in team settings JSON, not on the
      // base team row, so it's a separate PUT. Non-fatal if it fails:
      // the coach can still pick a sport later from Team Settings →
      // Team Info. We don't block on this.
      if (wizardForm.teamSport && wizardForm.teamSport !== 'hockey') {
        try {
          await api.put(`/teams/${data.id}`, {
            settings: { team_sport: wizardForm.teamSport },
          });
        } catch (err) {
          console.warn('Sport setting not saved (non-fatal):', err?.message);
        }
      }

      await loadTeams();
      setWizardStep(2);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to create team'); }
    setCreating(false);
  };

  // Apr 2026 — schedule upload handler. Matches ImportSchedulePage.jsx
  // flow but inline in the wizard for fast onboarding. The user picks
  // a PDF or image; we read it as base64 and POST to
  // /teams/:id/import-schedule/extract. The endpoint runs the AI
  // scheduleExtractor service and returns parsed games.
  // Inline status updates (uploading → extracting → done) so the coach
  // sees progress without leaving the wizard.
  const wizardUploadSchedule = async () => {
    if (!wizardTeam || !wizardScheduleFile) return;

    // File-type guard — same allowlist as the standalone import page.
    const ALLOWED = new Set(['image/jpeg','image/png','image/webp','image/gif','application/pdf']);
    if (!ALLOWED.has(wizardScheduleFile.type)) {
      setWizardScheduleStatus('error');
      setWizardScheduleError('Unsupported file type. Use a PDF, JPG, or PNG.');
      return;
    }
    if (wizardScheduleFile.size > 14 * 1024 * 1024) {
      setWizardScheduleStatus('error');
      setWizardScheduleError('File is too large (max 14 MB).');
      return;
    }

    setWizardScheduleStatus('uploading');
    setWizardScheduleError(null);
    try {
      // Read as base64 — endpoint expects JSON body with base64 data,
      // not multipart. (Same shape as ImportSchedulePage.)
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload  = e => { const url = e.target.result; resolve(url.split(',')[1]); };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(wizardScheduleFile);
      });
      setWizardScheduleStatus('extracting');
      const { data } = await api.post(
        `/teams/${wizardTeam.id}/import-schedule/extract`,
        {
          filename: wizardScheduleFile.name,
          mimeType: wizardScheduleFile.type,
          data:     base64,
          fileSize: wizardScheduleFile.size,
        },
        { timeout: 90_000 } // PDF extraction can take 30-60s
      );
      // Apr 30 2026 — store the FULL extracted result (games array,
      // provider, filename) not just the count. The review-and-save
      // step now happens inline in the wizard instead of bouncing the
      // coach to /games/import (which started a fresh wizard at step 1
      // and lost the extracted data — bug report from coach setup).
      setWizardScheduleResult({
        games: data?.games || [],
        gamesCount: data?.games?.length || 0,
        provider: data?.provider || 'AI',
        filename: wizardScheduleFile.name,
      });
      setWizardScheduleStatus('done');
    } catch (err) {
      setWizardScheduleStatus('error');
      setWizardScheduleError(err.response?.data?.error || err.message || 'Failed to extract schedule');
    }
  };

  // Apr 2026 — copy players from one of the coach's other teams.
  // Backend endpoint is /teams/:teamId/copy-members which we add in
  // this same patch (mirrors the existing season-level copy-roster
  // pattern). On success we show a toast and advance.
  const wizardCopyPlayers = async () => {
    if (!wizardTeam || !wizardCopyFromTeamId) return;
    setWizardCopyingPlayers(true);
    try {
      const { data } = await api.post(
        `/teams/${wizardTeam.id}/copy-members`,
        // 2026-08-07 — notifyPlayers: emails newly copied users a
        // "you've been added to <team>" (teamAdded) so existing
        // accounts aren't added silently anymore.
        { fromTeamId: wizardCopyFromTeamId, scope: wizardCopyScope, notifyPlayers: wizardNotifyCopied }
      );
      const n = data?.copied || 0;
      const emailed = data?.notified || 0;
      toast.success(`Copied ${n} ${n === 1 ? 'player' : 'players'}!` +
        (emailed > 0 ? ` Emailed ${emailed} that they've been added.` : ''));
      if (n > 0) setWizardPlayersImported(true); // step-4 checklist ✓
      setWizardStep(5);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to copy players');
    }
    setWizardCopyingPlayers(false);
  };

  // Apr 2026 — wizardSaveArena removed. The arena-collection step
  // was replaced by schedule-import in step 2 (arena info comes from
  // the parsed schedule rather than a separate form). The
  // wizardInvitePlayer one-at-a-time flow was also removed (step 3
  // now does bulk copy from another team or directs to the Players
  // page). Both helpers + their state (savingArena, wizardInvite)
  // are deleted to keep this file lean.

  const closeWizard = () => {
    setShowWizard(false); setWizardStep(1); setWizardTeam(null); setWizardStripe(null);
    setWizardType(null);   // 2026-10-18 — the next open asks the team kind again
    setWizardCopyFromTeamId('');
    setWizardNotifyCopied(true); // 2026-08-07 — reset (leak-prevention pattern)
    // 2026-07-10 (Phase 2.5) — settings-step state resets (leak-prevention,
    // same class of bug the Apr 30 audit caught for wizardReviewing).
    setWizardSettingsMode('keep');
    setWizardSettingsFromTeamId('');
    setWizardApplyingSettings(false);
    setWizardScheduleFile(null);
    setWizardScheduleStatus('idle');
    setWizardScheduleResult(null);
    setWizardScheduleError(null);
    // Apr 30 2026 — also reset the inline review flag so a coach
    // who closes the wizard mid-review doesn't reopen straight
    // into the review step on the next new-team flow. Forgetting
    // this state was caught in the post-edit audit.
    setWizardReviewing(false);
    // 2026-07-10 — seasonName + the players-imported flag reset too
    // (forgetting form keys here leaks values into the NEXT wizard run —
    // same class of bug the Apr 30 audit caught for wizardReviewing).
    setWizardPlayersImported(false);
    setWizardForm({
      name:'', leagueName:'', teamSport:'hockey', seasonName:'',
      gameDay:'Monday', gameTime:'20:00',
      arenaName:'', arenaAddress:'',
    });
  };

  // 2026-07-10 (wizard v2) — FINISH: full page load into the new team.
  // Coach-reported bug: after creating a team, sidebar items (Players…)
  // were missing until a manual refresh. A reload rebuilds every layout
  // query against the new team; gd:lastTeamId makes boot pick it.
  const finishWizard = () => {
    if (wizardTeam) {
      try { localStorage.setItem('gd:lastTeamId', wizardTeam.id); } catch {}
    }
    window.location.assign('/dashboard');
  };

  // 2026-07-10 (Phase 2.6, coach request) — bulk AI import from wizard
  // step 3: land straight in the Players importer (PDF/screenshot/CSV —
  // the same AI workflow as the Players page) for the NEW team. Full
  // page load so the importer boots with the fresh team + its season;
  // imported players auto-attach to the active season (Phase 1.1).
  const wizardGoToImporter = () => {
    if (!wizardTeam) return;
    try { localStorage.setItem('gd:lastTeamId', wizardTeam.id); } catch {}
    window.location.assign(`/team/${wizardTeam.id}/players/import`);
  };

  // 2026-07-10 (wizard v2) — ABORT via the ✕ button. Semantics per Gilles:
  // "cancel creating the team." Step 1 (nothing created) → just close.
  // Steps 2-4 (team exists, mid-setup; Phase 2.5 made Settings step 2) →
  // confirm, DELETE the team, close.
  // Step 5 (fully set up) → treat as finish; deleting a completed team
  // from a stray ✕ would be brutal.
  const wizardAbort = async () => {
    if (!wizardTeam) { closeWizard(); return; }
    if (wizardStep === 5) { finishWizard(); return; }
    const sure = window.confirm(
      `Cancel creating "${wizardTeam.name}"? This deletes the team and anything imported so far.`
    );
    if (!sure) return;
    try {
      await api.delete(`/teams/${wizardTeam.id}`);
      toast.success('Team creation cancelled');
    } catch {
      toast.error('Could not delete the team — you can remove it later from Team Settings.');
    }
    closeWizard();
    loadTeams(user?.last_active_team_id ?? null);
  };

  const getGameLabel = (dateStr) => {
    const d = new Date(dateStr.slice(0,10) + 'T12:00:00');
    if (isToday(d))    return <span style={{ color:'var(--goal)' }}>Today</span>;
    if (isTomorrow(d)) return <span style={{ color:'var(--puck)' }}>Tomorrow</span>;
    const days = differenceInDays(d, new Date());
    if (days <= 7) return <span className="text-ice">In {days}d</span>;
    return <span className="text-secondary">{format(d,'MMM d')}</span>;
  };

  const sColors = { in:'status-in', out:'status-out', pending:'status-pending' };
  const wf = (k) => (e) => setWizardForm(p => ({ ...p, [k]: e.target.value }));

  const WizardDots = () => (
    <div className="flex items-center gap-1 mb-6">
      {/* 2026-07-10 (Phase 2.5) — 5 dots: Settings inserted as step 2. */}
      {[1,2,3,4,5].map((n,i) => (
        <div key={n} className="flex items-center gap-1 flex-1">
          <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
            style={{
              background: wizardStep > n ? 'var(--goal)' : wizardStep === n ? 'var(--ice)' : 'var(--surface-3)',
              color: wizardStep >= n ? '#000' : 'var(--text-secondary)',
            }}>
            {wizardStep > n ? '✓' : n}
          </div>
          {i < 4 && <div className="flex-1 h-px" style={{ background: wizardStep > n ? 'var(--goal)' : 'var(--border)' }} />}
        </div>
      ))}
    </div>
  );
  // Detect today's game
  const todayGame = upcomingGames.find(g => isToday(parseISO(g.game_date)));

  return (
    <div className="animate-fade-in">

      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-4">
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{
            fontFamily: 'var(--font-display)', fontSize: 10,
            letterSpacing: '0.2em', textTransform: 'uppercase',
            color: 'var(--ice)', marginBottom: 4,
          }}>
            {new Date().toLocaleDateString('en-CA',{weekday:'long',month:'long',day:'numeric'}).toUpperCase()}
          </p>
          <h1 style={{
            fontFamily: 'var(--font-display)', fontWeight: 900, fontStyle: 'italic',
            fontSize: 'clamp(28px, 7vw, 48px)', lineHeight: 0.9,
            letterSpacing: '-0.01em', color: 'var(--text)',
            textTransform: 'uppercase',
          }}>
            {todayGame ? '🚨 GAME DAY' : `Hey,\n${user?.name?.split(' ')[0] || 'Champ'}`}
          </h1>
          <p style={{ color: 'var(--text-2)', marginTop: 6, fontSize: 13 }}>
            {activeTeam
              ? activeTeam.name
              /* Empty-dashboard subheader: avoid prompting users to
                 "create a team" since most are invited players, not
                 coaches. Instead, point them at where their team/
                 schedule will show up once their invite is applied.
                 (Apr 2026 — see empty-state card below.) */
              : 'Welcome to Game Day — your team and schedule will appear here once you accept an invite.'}
          </p>
        </div>
        {/* "+ New Team" / "+ New Season" button — Apr 2026 contextual.
            Previously this was always "+ New Team" so brand-new coaches
            were nudged to create yet another team. Coaches reported this
            was confusing right after the wizard since the natural next
            step is creating a SEASON to hold games — not making more
            teams. So when the active team has zero seasons, the CTA
            swaps to "+ New Season" and routes to the Seasons page
            (which has the create-season modal). With seasons present
            (or no active team at all) the CTA stays as "+ New Team"
            so multi-team coaches can still spin up another squad.

            v2.5.90 (May 8 2026) — coach feedback: "Please remove the
            purple plus button that creates a new team for the mobile
            app. Let's just keep that buried somewhere else more on the
            desktop." Wrapped the button in `hidden sm:block` so it's
            CSS-hidden on phones and visible on tablets/desktop. Mobile
            users can still reach team creation via Schedule → Add or
            the More menu. The wrapper stays inside the same flex
            row — when hidden it collapses to zero width, so the
            heading reflows to fill the row naturally. */}
        <div className="hidden sm:block">
          {(() => {
            const isCoachish = activeTeam && ['coach', 'manager'].includes(activeTeam.member_role);
            // null = seasons not loaded yet → fall through to default
            // "+ New Team" so we don't flash the wrong label. Empty
            // array (loaded, no seasons) is what triggers the swap.
            const noSeasonsYet = isCoachish && Array.isArray(seasons) && seasons.length === 0;
            if (noSeasonsYet) {
              return (
                <button
                  onClick={() => navigate(`/team/${activeTeam.id}/seasons`)}
                  className="btn-ice"
                  style={{ padding: '9px 14px', fontSize: 12, flexShrink: 0, borderRadius: 12 }}
                  title="Create your first season — group games together for stats and roster">
                  <Plus size={15} /><span className="hidden sm:inline">New Season</span>
                </button>
              );
            }
            // 2026-09-16 (per Gilles) — "+ New Team" is gone from the
            // header: coaches get a one-tap "Message Players" here instead
            // (it messages the active team's current season roster — same
            // composer as the card further down). Creating another team
            // still lives in the sidebar's "+ New Team" entry. Players
            // (non-coach) get nothing here — they never needed New Team.
            if (!isCoachish) return null;
            return (
              <button onClick={openMessagePlayers}
                className="btn-ice"
                style={{ padding: '9px 14px', fontSize: 12, flexShrink: 0, borderRadius: 12 }}
                title="Message the players on this team">
                <Mail size={15} /><span className="hidden sm:inline">Message Players</span>
              </button>
            );
          })()}
        </div>
      </div>

      {/* ── League announcements banner — v2.5.122 ──
          Renders unread announcements addressed to this team's
          league. Silent no-op for teams without a league. The
          component handles its own data fetch + dismissal —
          DashboardPage doesn't need to know anything about it. */}
      {activeTeam?.id && <LeagueAnnouncementsBanner teamId={activeTeam.id} />}

      {/* ── Upcoming Reminders ──
          Apr 2026 — moved to immediately under the Next Game hero
          (see further below in the coach tile flow). Keeping the old
          spot here would have meant the same component could appear
          in two places when the layout changes. The loader still
          short-circuits to [] for non-coach roles. */}

      {/* ── GAME DAY HERO ── */}
      {todayGame && !loading && (
        <div className="gameday-hero p-5 mb-4 animate-pop">
          <div className="flex flex-col gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full animate-pulse" style={{ background:'var(--fire)' }} />
                <span className="text-xs font-bold tracking-widest" style={{ color:'var(--fire)', fontFamily:'var(--font-display)' }}>TONIGHT</span>
              </div>
              <h2 className="section-title text-2xl mb-1">
                {format(parseISO(todayGame.game_date),'EEEE, MMMM d').toUpperCase()}
              </h2>
              <div className="flex flex-wrap items-center gap-3 text-sm mt-2" style={{ color:'var(--text-secondary)' }}>
                {todayGame.game_time && (
                  <span>🕐 {formatTime(todayGame.game_time)}
                    {/* Apr 30 2026 (App_Change_List #18) — show end time
                        when we can compute it. End time is start +
                        duration, formatted through the same 12h/24h
                        formatter so it matches the start time's style.
                        Suppressed cleanly when duration is unknown
                        (e.g. team has no default and per-game value
                        is null). */}
                    {(() => {
                      const durMin = todayGame.duration_minutes
                        || activeTeam?.settings?.default_game_duration_minutes;
                      const dur     = formatDuration(durMin);
                      const endTime = computeEndTime(todayGame.game_time, durMin);
                      return (
                        <>
                          {endTime && <> – {formatTime(endTime)}</>}
                          {dur && <span style={{ opacity: 0.75 }}> · {dur}</span>}
                        </>
                      );
                    })()}
                  </span>
                )}
                {todayGame.arena_name && <span>📍 {todayGame.arena_name}</span>}
              </div>
            </div>

            {/* v2.5.97 — five-stat row (IN / GOALIES / OUT / WAIT
                LIST / NO RESPONSE) on the TONIGHT card. Coach
                feedback: "We need to see all the stats even for the
                game that is happening today." Pulled fields from
                todayGame (already returned by the games endpoint).
                Mirrors the layout of the NEXT GAME card below so
                both cards read consistently. */}
            {(() => {
              const tIn      = parseInt(todayGame.players_in)        || 0;
              const tOut     = parseInt(todayGame.players_out)       || 0;
              const tWait    = parseInt(todayGame.players_waitlist)  || 0;
              // v2.5.147 — denominator switched from total_members
              // (all roster) to invited_count (FT + spares actually
              // invited for THIS game). Schedule page does the same;
              // both badges now agree on the same number for the
              // same game. Nullish-aware fallback to total_members
              // during a mid-deploy cache gap (a true 0 invited_count
              // doesn't silently re-introduce the old bug).
              const tTotal   = todayGame.invited_count != null
                                 ? (parseInt(todayGame.invited_count) || 0)
                                 : (parseInt(todayGame.total_members) || 0);
              const tGoalies = parseInt(todayGame.goalies_in)        || 0;
              const tNoResp  = Math.max(0, tTotal - tIn - tOut - tWait);
              const tSkaters = Math.max(0, tIn - tGoalies);
              const Stat = ({ value, label, color }) => (
                <div className="flex flex-col items-center" style={{ minWidth: 0, flex: '1 1 0' }}>
                  <span className="text-2xl font-extrabold leading-none" style={{ color, fontFamily: 'var(--font-display)', fontVariantNumeric: 'tabular-nums' }}>
                    {value}
                  </span>
                  <span className="text-[10px] uppercase tracking-widest mt-1" style={{ color: 'var(--text-2)', fontFamily: 'var(--font-display)', fontWeight: 700 }}>
                    {label}
                  </span>
                </div>
              );
              return (
                <div className="flex items-center justify-between gap-2"
                  style={{
                    padding: '10px 4px',
                    borderTop: '1px solid var(--border)',
                    borderBottom: '1px solid var(--border)',
                  }}>
                  <Stat value={tSkaters} label="IN" color="var(--goal)" />
                  <Stat value={`🥅 ${tGoalies}`} label="Goalies" color="var(--goal)" />
                  <Stat value={tOut}     label="OUT"       color="var(--penalty)" />
                  <Stat value={tWait}    label="Wait List" color="#a78bfa" />
                  <Stat value={tNoResp}  label="—"         color="var(--text-2)" />
                </div>
              );
            })()}

            {/* May 2 2026 — was an embedded ScoreboardCard. Per
                coach feedback the live scoreboard now lives on its
                own dedicated page so it's not buried inside the
                today-card. Link button takes the user there with
                the team and today's game preselected. The standalone
                page renders the same component but without the
                surrounding RSVP / status chrome competing for attention. */}
            {activeTeam?.id && (
              <button
                type="button"
                onClick={() => navigate(`/team/${activeTeam.id}/games/${todayGame.id}/scoreboard`)}
                className="btn-ghost w-full justify-center"
                style={{ padding: '12px 14px', fontSize: 13, fontWeight: 700 }}>
                <Trophy size={15} /> Live Scoreboard →
              </button>
            )}
            {/* Sep 2026 — Phase 7C: voice stat recorder entry, coach/
                manager only. Same target as the scoreboard page's
                Record button. */}
            {activeTeam?.id && (activeTeam.member_role === 'coach' || (Array.isArray(activeTeam.settings?.stat_recorders) && activeTeam.settings.stat_recorders.includes(user?.id))) && (
              <button
                type="button"
                onClick={() => navigate(`/team/${activeTeam.id}/game/${todayGame.parent_game_id || todayGame.id}/live`)}
                className="w-full justify-center"
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '12px 14px', fontSize: 13, fontWeight: 700, borderRadius: 12,
                  background: 'rgba(239,68,68,0.14)', border: '1px solid rgba(239,68,68,0.45)',
                  color: 'var(--text)', cursor: 'pointer',
                }}>
                🎙 Record &amp; track live →
              </button>
            )}

            <div className="flex flex-col gap-2">
              <p className="text-xs font-bold tracking-widest mb-0.5" style={{ color:'var(--text-secondary)', fontFamily:'var(--font-display)' }}>YOUR STATUS</p>
              {/* May 2 2026 — handle the full status set, not just in/out.
                  Was: always rendered I'M IN + OUT buttons regardless of
                  current status, which let users on the waitlist (or in
                  pending_payment) silently overwrite their state by
                  tapping I'M IN. The dashboard would say "in" but the
                  game page still showed the waitlist row — out-of-sync.
                  Now: when on waitlist or pending_payment, show a
                  read-only badge and route the user to the game page
                  where the contextual flow lives (FCFS, Pay Now,
                  promote/demote, etc. — all that game-state-specific
                  logic doesn't belong on the dashboard). */}
              {(todayGame.my_status === 'waitlist' || todayGame.my_status === 'pending_payment') ? (
                <>
                  <div
                    className="flex-1 py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-2"
                    style={{
                      background: 'rgba(167,139,250,0.10)',
                      color: '#a78bfa',
                      border: '1px solid rgba(167,139,250,0.35)',
                      fontFamily: 'var(--font-display)',
                      letterSpacing: '0.06em',
                      padding: '12px 14px',
                    }}>
                    ⏳ {todayGame.my_status === 'pending_payment' ? 'Awaiting Payment' : 'On Waitlist'}
                  </div>
                  <p className="text-xs text-secondary text-center" style={{ marginTop: -2, marginBottom: 2 }}>
                    Open the game to manage your waitlist spot.
                  </p>
                </>
              ) : (
                <div className="flex gap-2">
                  {[
                    { s:'in',    label:"I'M IN",  emoji:'🏟', color:'var(--goal)' },
                    { s:'out',   label:"OUT",      emoji:'✌️', color:'var(--penalty)' },
                  ].map(opt => (
                    <button key={opt.s}
                      onClick={e => rsvp(e, todayGame.id, opt.s)}
                      disabled={!!rsvping[todayGame.id]}
                      className="flex-1 py-2.5 rounded-xl font-bold text-xs transition-all active:scale-95"
                      style={{
                        background: todayGame.my_status === opt.s ? opt.color : 'var(--surface-2)',
                        color:      todayGame.my_status === opt.s ? '#000'     : opt.color,
                        border:     `1px solid ${todayGame.my_status === opt.s ? opt.color : `${opt.color}30`}`,
                        fontFamily: 'var(--font-display)',
                        letterSpacing: '0.06em',
                        WebkitTapHighlightColor: 'transparent',
                      }}>
                      {opt.emoji} {opt.label}
                    </button>
                  ))}
                </div>
              )}
              <button onClick={() => navigate(`/game/${todayGame.id}`)}
                className="btn-ice w-full justify-center text-xs mt-0.5"
                style={{ fontFamily:'var(--font-display)', letterSpacing:'0.08em', padding: '10px 16px' }}>
                VIEW GAME DETAILS →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Empty state ──
          May 1 2026 — gate on teamsLoaded so we don't flash this card
          during the brief gap between mount and the layout's /teams
          fetch resolving. Previously: refreshing the dashboard for a
          user who DOES have teams briefly showed "You're not on a
          schedule yet" because teams=[] (initial layout state) and
          loading=false (DashboardPage's own effect flips it off
          quickly when activeTeam is null) both happened before
          /teams resolved. teamsLoaded is set true by the layout's
          loadTeams() in a finally block — by then teams contains
          the real list. */}
      {teams.length === 0 && !loading && teamsLoaded && (() => {
        // ── Empty-state rewrite (Apr 2026) ──
        // Primary assumption: almost everyone on Game Day is an invited
        // player, not a coach bootstrapping a new team. Showing a big
        // "Create Your Team" CTA was misleading — multiple users
        // reported confusion after clicking an invite link, logging
        // in, and being prompted to "create a team" when they were
        // just supposed to join the one their coach had already set
        // up. The empty state now assumes the user is waiting for
        // an invite (or has a pending one that failed to apply), and
        // only mentions team creation as a discreet footnote for the
        // rare coach case.
        //
        // Three states:
        //   1. pendingInviteToken in localStorage → invite retry card
        //   2. no token → "you're not linked to a schedule yet" card
        //   3. coach escape hatch → small link at the bottom that
        //      opens the New Team wizard
        const pendingInviteToken = localStorage.getItem('pendingInviteToken');
        if (pendingInviteToken) {
          return (
            <div className="zen-card text-center py-14 px-6 mb-4">
              <div style={{ fontSize: 52, marginBottom: 12 }}>📬</div>
              <h2 className="section-title text-xl mb-3">You Have an Invite</h2>
              <p style={{ color:'var(--text-secondary)' }} className="mb-6 max-w-sm mx-auto">
                We couldn't add you to your team automatically. Tap below to try again, or ask your coach for a fresh invite link.
              </p>
              <div className="flex flex-col gap-2 items-center">
                <button
                  onClick={() => navigate(`/invite/${pendingInviteToken}`)}
                  className="btn-ice text-base py-3 px-8">
                  Open My Invite →
                </button>
                <button
                  onClick={() => {
                    localStorage.removeItem('pendingInviteToken');
                    // Force re-render by flipping a state — simplest is
                    // reload so the dashboard re-evaluates the empty state.
                    window.location.reload();
                  }}
                  className="btn-ghost text-sm py-2 px-4 mt-2"
                  style={{ fontSize: 12 }}>
                  Dismiss invite and start fresh
                </button>
              </div>
            </div>
          );
        }
        // No pending invite — they're not linked to anything. This is
        // the most common "blank slate" state for an invited player
        // whose invite already succeeded but whose coach hasn't
        // added them to a schedule yet (e.g., coach created the team
        // then invited them before setting up a season), or for a
        // user who signed up directly without going through an invite.
        return (
          <div className="zen-card text-center py-14 px-6 mb-4">
            <div style={{ fontSize: 52, marginBottom: 12 }}>⏳</div>
            <h2 className="section-title text-xl mb-3">You're Not on a Schedule Yet</h2>
            <p style={{ color:'var(--text-secondary)', lineHeight: 1.6 }} className="mb-6 max-w-sm mx-auto">
              Ask your coach for an invite link. Once you accept, your team and schedule will appear here.
            </p>
            <p style={{ color:'var(--text-3)', fontSize: 12 }} className="mb-6 max-w-sm mx-auto">
              If your coach already invited you but this page is empty, they may not have added you to a schedule yet — check back later, or reach out to them.
            </p>
            {/* Apr 2026 — second CTA: start your own team.
                Previously worded as "Are you a coach? Create a
                team", which gated the option in the user's mind to
                "only coaches". Reworded so any player knows they
                can spin up their own team (for a pickup game, a
                second team, a one-off tournament, etc.) while
                staying on whatever teams invited them. The creator
                automatically becomes the coach of the new team. */}
            <div style={{ height: 1, background: 'var(--border)', margin: '18px auto', maxWidth: 240 }} />
            <p style={{ color:'var(--text-2)', fontSize: 12, marginBottom: 10 }}>
              Want to set up your own team?
            </p>
            <button
              onClick={() => { setShowWizard(true); setWizardStep(1); }}
              className="btn-ice"
              style={{ fontSize: 13, padding: '10px 18px' }}>
              <Plus size={14} /> Start a New Team
            </button>
            <p style={{ color:'var(--text-3)', fontSize: 11, marginTop: 8 }} className="max-w-sm mx-auto">
              You'll be the coach of the team you create. You can still stay on any other teams you're invited to.
            </p>
          </div>
        );
      })()}

      {activeTeam && (
        <>
          {/* ── Sport Stats Bar — role-aware ──
              Coach view: next game / roster gap / RSVPs pending / action items
              Player view: next game / my RSVP status / games played / roster size
              Each tile surfaces something actionable or at least currently relevant. */}
          {(() => {
            // Apr 2026 — when a game is today (rendered in the
            // Game Day Hero above), the "Next Game" hero should look
            // PAST it to the next upcoming game. Otherwise the same
            // game shows twice on the dashboard. If there's nothing
            // after today, the hero falls back to today's game so we
            // don't render an empty "no game" placeholder while the
            // Game Day Hero shows tonight.
            const todayGame = upcomingGames.find(g => isToday(parseISO(g.game_date)));
            const nextGame = todayGame
              ? (upcomingGames.find(g => !isToday(parseISO(g.game_date))) || null)
              : (upcomingGames[0] || null);
            const isCoachRole = ['coach', 'manager'].includes(activeTeam.member_role);

            // Countdown string for next game — "in 3d" or "today" or "tomorrow"
            const countdownToNext = (g) => {
              if (!g) return '';
              const gd = new Date(g.game_date.slice(0, 10) + 'T12:00:00');
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const diffDays = Math.round((gd - today) / 86400000);
              if (diffDays === 0) return 'today';
              if (diffDays === 1) return 'tomorrow';
              if (diffDays < 0)   return 'in progress';
              return `in ${diffDays}d`;
            };

            // Next game date label (short — "Apr 26" or "Sun 26")
            const nextGameLabel = (g) => {
              if (!g) return '—';
              try {
                const gd = new Date(g.game_date.slice(0, 10) + 'T12:00:00');
                return gd.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' });
              } catch { return '—'; }
            };

            // Min players to play — team setting, default 10 matches backend
            const minPlayers = parseInt(activeTeam.settings?.min_players_to_play) || 10;

            // ─────────────────────────────────────────────────────────────
            // UNIFIED HERO PATH — both coaches and players see the
            // same Next Game hero card with the 5-stat row (IN /
            // GOALIES / OUT / WAIT LIST / NO RESPONSE). Player-
            // specific bits (RSVP status banner, no "Schedule a Game"
            // CTA) layered in after the hero. Coach-specific bits
            // (scheduled reminders, "Short by" warning) gated on
            // isCoachRole.
            //
            // May 1 2026 — was a separate 4-tile grid for players
            // (NEXT GAME / MY STATUS / GAMES PLAYED / PLAYERS) which
            // hid the more useful coach-style overview. Coaches and
            // players now share one surface.
            // ─────────────────────────────────────────────────────────────
            // Stats for next game
            const playersIn = nextGame ? parseInt(nextGame.players_in) || 0 : 0;
            const playersOut = nextGame ? parseInt(nextGame.players_out) || 0 : 0;
            const playersWait = nextGame ? parseInt(nextGame.players_waitlist) || 0 : 0;
            const playersPending = nextGame ? parseInt(nextGame.players_pending) || 0 : 0;
            // v2.5.147 — totalRoster denominator switched from
            // total_members to invited_count. See SchedulePage comment.
            // Nullish-aware fallback so a true 0 invited_count
            // doesn't silently fall back to total_members.
            const totalRoster = nextGame
              ? (nextGame.invited_count != null
                   ? (parseInt(nextGame.invited_count) || 0)
                   : (parseInt(nextGame.total_members) || 0))
              : 0;
            const noResp = nextGame
              ? Math.max(0, totalRoster - playersIn - playersOut - playersWait)
              : 0;
            const goaliesIn = nextGame ? parseInt(nextGame.goalies_in) || 0 : 0;
            const skatersIn = Math.max(0, playersIn - goaliesIn);
            // v2.5.226 — "need N to play" is a SKATER minimum; the goalie is
            // counted separately (the GOALIES stat). Was `minPlayers - playersIn`,
            // which folded the goalie into the skater count (8 skaters + 1 goalie
            // = 9 → "short by 1" instead of the correct "short by 2"). Use
            // skatersIn so the warning matches the IN (skaters) stat shown.
            const shortBy = Math.max(0, minPlayers - skatersIn);

            const heroDateLabel = nextGame
              ? (() => {
                  const gd = new Date(nextGame.game_date.slice(0, 10) + 'T12:00:00');
                  return gd.toLocaleDateString('en-US', {
                    weekday: 'short', month: 'short', day: 'numeric',
                  }).toUpperCase();
                })()
              : null;

            return (
                <>
                  {/* ── HERO NEXT GAME CARD (Apr 2026, Tom_Notes A1+A2+A4+A5+A6) ──
                      Replaces the old "Next Game" tile that was just one
                      of four small grid items. Now it's the lead surface
                      on the coach dashboard:
                        • clickable → navigates straight to the game page
                        • bigger numbers (skater + goalie split prominent)
                        • "X over min" subtext gone — coaches can read
                          the IN count without a redundant differential
                        • Date / time / arena up top so it reads like
                          the game card it represents */}
                  {nextGame ? (
                    <>
                    <button
                      type="button"
                      onClick={() => navigate(`/game/${nextGame.id}`)}
                      className="card animate-pop w-full text-left"
                      style={{
                        padding: '18px 18px 16px',
                        borderTop: '4px solid var(--ice)',
                        borderRadius: '0 0 16px 16px',
                        background: 'var(--surface)',
                        position: 'relative', overflow: 'hidden',
                        cursor: 'pointer', display: 'block',
                        marginBottom: 12,
                        WebkitTapHighlightColor: 'transparent',
                      }}>
                      {/* subtle gradient glow at the top edge */}
                      <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, height: 60,
                        background: 'linear-gradient(to bottom, rgba(0,212,255,0.10), transparent)',
                        pointerEvents: 'none',
                      }} />
                      {/* eyebrow + date row */}
                      <div className="flex items-center justify-between mb-1.5"
                        style={{ position: 'relative', zIndex: 1 }}>
                        <span style={{
                          fontFamily: 'var(--font-display)', fontSize: 10,
                          letterSpacing: '0.18em', color: 'var(--text-2)',
                          textTransform: 'uppercase', fontWeight: 700,
                        }}>Next Game</span>
                        {/* Apr 30 2026 — countdown badge upsized for readability.
                            Was tiny 10px text in --text-3 (faded). Coaches with
                            older eyes were missing it entirely. Now a bigger,
                            higher-contrast pill in ice blue with a tinted
                            background so it actually catches the eye. Hierarchy
                            preserved: the date itself (clamp 22-30px) is still
                            visually dominant; this just escalates from 10px to
                            14-15px and adds chrome. */}
                        <span style={{
                          fontFamily: 'var(--font-display)',
                          fontSize: 'clamp(13px, 2.5vw, 15px)',
                          letterSpacing: '0.06em',
                          fontWeight: 800,
                          color: 'var(--ice)',
                          background: 'rgba(0,212,255,0.10)',
                          border: '1px solid rgba(0,212,255,0.25)',
                          padding: '4px 10px',
                          borderRadius: 999,
                          textTransform: 'uppercase',
                          whiteSpace: 'nowrap',
                        }}>{countdownToNext(nextGame)} →</span>
                      </div>
                      <div style={{
                        fontFamily: 'var(--font-display)', fontWeight: 900,
                        fontStyle: 'italic',
                        // Bigger than the old tile (was clamp 20–32). Numbers + date deserve more weight.
                        fontSize: 'clamp(22px, 5.5vw, 30px)',
                        lineHeight: 1.05, color: 'var(--ice)',
                        letterSpacing: '-0.01em', marginBottom: 4,
                        position: 'relative', zIndex: 1,
                      }}>
                        {heroDateLabel}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs mt-1 mb-3"
                        style={{ color: 'var(--text-secondary)', position: 'relative', zIndex: 1 }}>
                        {nextGame.game_time && (
                          <span>🕐 {formatTime(nextGame.game_time)}
                            {/* Apr 30 2026 (App_Change_List #18) — end
                                time + duration, same pattern as today. */}
                            {(() => {
                              const durMin = nextGame.duration_minutes
                                || activeTeam?.settings?.default_game_duration_minutes;
                              const dur     = formatDuration(durMin);
                              const endTime = computeEndTime(nextGame.game_time, durMin);
                              return (
                                <>
                                  {endTime && <> – {formatTime(endTime)}</>}
                                  {dur && <span style={{ opacity: 0.75 }}> · {dur}</span>}
                                </>
                              );
                            })()}
                          </span>
                        )}
                        {nextGame.arena_name && <span>📍 {nextGame.arena_name}</span>}
                      </div>

                      {/* Big-number stat row — IN (skaters + goalie split),
                          OUT, WAIT LIST, no-response. Mirrors the old
                          rosterExtra block but at hero scale. Goalies
                          count is its OWN slot now per Tom A4 — coaches
                          glanced past the "🥅 N" inline marker before. */}
                      <div className="flex items-stretch justify-between gap-1.5 mt-2 pt-3"
                        style={{
                          borderTop: '1px dashed var(--border)',
                          position: 'relative', zIndex: 1,
                        }}>
                        {[
                          // 2026-09-16 (per Gilles) — was "In"; renamed "Players" with a
                          // player icon so it reads as the skater count beside the
                          // goalie count (which already carried 🥅). Same 🏒 icon is
                          // used on the Schedule page's check-in count.
                          { val: skatersIn,  color: 'var(--goal)',    label: 'Players', emoji: '🏒' },
                          { val: goaliesIn,  color: 'var(--goal)',    label: 'Goalies', emoji: '🥅' },
                          { val: playersOut, color: 'var(--penalty)', label: 'Out'       },
                          { val: playersWait,color: '#a78bfa',         label: 'Wait List' },
                          { val: noResp,     color: 'var(--text-2)',   label: '—'         },
                        ].map(({ val, color, label, emoji }) => (
                          <div key={label}
                            className="flex flex-col items-center"
                            style={{
                              flex: '1 1 0', minWidth: 0,
                              // keep tight gaps; numbers should breathe
                              padding: '0 1px',
                            }}>
                            <span style={{
                              // Bigger than old extra row (was 15) — A6 "make numbers bigger"
                              fontSize: 'clamp(18px, 4.5vw, 24px)',
                              fontWeight: 800, lineHeight: 1, color,
                              fontFamily: 'var(--font-display)',
                              fontStyle: 'italic',
                              display: 'flex', alignItems: 'baseline', gap: 3,
                            }}>
                              {emoji && <span style={{ fontSize: '0.65em', fontStyle: 'normal' }}>{emoji}</span>}
                              {val}
                            </span>
                            <span style={{
                              fontSize: 9, color: 'var(--text-2)',
                              textTransform: 'uppercase', letterSpacing: '0.06em',
                              marginTop: 4, fontWeight: 700, fontFamily: 'var(--font-display)',
                              whiteSpace: 'nowrap',
                            }}>
                              {label}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* "Short by N" warning when below min —
                          coach-only since it nudges them to chase up
                          RSVPs / invite spares (a player can't act on
                          this anyway). */}
                      {isCoachRole && shortBy > 0 && (
                        <div className="mt-3 px-2.5 py-1.5 rounded-md text-xs flex items-center gap-1.5"
                          style={{
                            background: 'rgba(255,68,68,0.08)',
                            border: '1px solid rgba(255,68,68,0.20)',
                            color: 'var(--penalty)', fontWeight: 700,
                            position: 'relative', zIndex: 1,
                          }}>
                          ⚠ Short by {shortBy} — need {minPlayers} to play
                        </div>
                      )}
                    </button>
                    {/* 2026-09-16 (per Gilles) — coach quick actions right under
                        the Next Game card: Invite Spares + Jab Players. Both
                        deep-link into the game page with ?open=… which opens
                        the matching modal on load (GamePage autoOpen effect),
                        so the dashboard doesn't have to duplicate the pickers. */}
                    {isCoachRole && (
                      <div className="flex gap-2 mb-3" style={{ marginTop: -4 }}>
                        <button
                          type="button"
                          onClick={() => navigate(`/game/${nextGame.id}?open=spares`)}
                          className="flex-1 flex items-center justify-center gap-1.5"
                          style={{
                            padding: '10px 12px', borderRadius: 12, fontSize: 12.5, fontWeight: 700,
                            background: 'var(--puck-a8)', border: '1px solid var(--puck-a15)', color: 'var(--puck)',
                            cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                          }}
                          title="Pick spares to invite to this game">
                          <Users size={14} /> Invite Spares
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate(`/game/${nextGame.id}?open=jab`)}
                          className="flex-1 flex items-center justify-center gap-1.5"
                          style={{
                            padding: '10px 12px', borderRadius: 12, fontSize: 12.5, fontWeight: 700,
                            background: 'var(--ice-a8)', border: '1px solid var(--ice-a15)', color: 'var(--ice)',
                            cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                          }}
                          title="Nudge everyone who hasn't responded yet">
                          <Zap size={14} /> Jab Players
                        </button>
                      </div>
                    )}
                    </>
                  ) : !todayGame ? (
                    /* No upcoming games at all — show placeholder so
                        the dashboard structure stays consistent (no
                        hero-shaped hole when the team is between
                        seasons). When a game IS today, the Game Day
                        Hero above already covers that surface, so we
                        suppress this empty placeholder to avoid
                        cluttering the page with two stacked cards
                        about the same lack of future games. */
                    <div className="card mb-3"
                      style={{
                        padding: '18px 18px',
                        borderTop: '4px solid var(--text-3)',
                        borderRadius: '0 0 16px 16px',
                      }}>
                      <span style={{
                        fontFamily: 'var(--font-display)', fontSize: 10,
                        letterSpacing: '0.18em', color: 'var(--text-2)',
                        textTransform: 'uppercase', fontWeight: 700,
                      }}>Next Game</span>
                      <div style={{
                        fontFamily: 'var(--font-display)', fontWeight: 900,
                        fontStyle: 'italic', fontSize: 'clamp(20px, 5vw, 28px)',
                        lineHeight: 1.05, color: 'var(--text-3)', marginTop: 6,
                      }}>None scheduled</div>
                      <p className="text-xs mt-2" style={{ color: 'var(--text-2)' }}>
                        {isCoachRole
                          ? 'Add games from the Schedule tab to see the next one here.'
                          : "Your coach hasn't scheduled a game yet. You'll be notified."}
                      </p>
                    </div>
                  ) : null}

                  {/* ── Player RSVP status banner (May 1 2026) ──
                      Players asked for an explicit Check-in / Check-out /
                      No-response indicator on the dashboard so they don't
                      have to drill into the game to see where they stand.
                      Coach doesn't get this — they don't typically RSVP
                      for themselves on the dashboard, and the hero stats
                      above already cover roster overview. */}
                  {!isCoachRole && nextGame && (() => {
                    const myStatus = nextGame.my_status || null;
                    const myTeam = nextGame.my_team_assignment || null;
                    const teamLabel = myTeam
                      ? (myTeam === 'home'
                          ? (nextGame.home_team_label || 'Home')
                          : (nextGame.away_team_label || 'Away'))
                      : null;
                    const variants = {
                      'in':       { label: "You're IN",          emoji: '✅', color: 'var(--goal)',    bg: 'rgba(0,232,122,0.10)',   border: 'rgba(0,232,122,0.30)' },
                      'out':      { label: "You're OUT",         emoji: '❌', color: 'var(--penalty)', bg: 'rgba(255,68,68,0.08)',   border: 'rgba(255,68,68,0.30)'  },
                      'waitlist': { label: "You're on WAITLIST", emoji: '⏳', color: '#a78bfa',         bg: 'rgba(167,139,250,0.10)', border: 'rgba(167,139,250,0.30)' },
                      'maybe':    { label: "You replied MAYBE",  emoji: '❓', color: 'var(--puck)',    bg: 'rgba(255,149,0,0.10)',   border: 'rgba(255,149,0,0.30)'   },
                    };
                    const v = variants[myStatus] || {
                      label: 'NO RESPONSE — tap to RSVP',
                      emoji: '⚠️',
                      color: 'var(--puck)',
                      bg: 'rgba(255,149,0,0.10)',
                      border: 'rgba(255,149,0,0.35)',
                    };
                    return (
                      <button
                        type="button"
                        onClick={() => navigate(`/game/${nextGame.id}`)}
                        className="w-full text-left mb-3"
                        style={{
                          background: v.bg,
                          border: `1.5px solid ${v.border}`,
                          borderRadius: 12,
                          padding: '12px 14px',
                          cursor: 'pointer',
                          WebkitTapHighlightColor: 'transparent',
                          display: 'flex', alignItems: 'center', gap: 12,
                        }}>
                        <span style={{ fontSize: 22, lineHeight: 1 }}>{v.emoji}</span>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{
                            display: 'block',
                            fontFamily: 'var(--font-display)',
                            fontWeight: 800,
                            fontSize: 14,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            color: v.color,
                            lineHeight: 1.2,
                          }}>{v.label}</span>
                          <span style={{
                            display: 'block',
                            fontSize: 11,
                            color: 'var(--text-2)',
                            marginTop: 2,
                          }}>
                            {teamLabel
                              ? `Assigned to ${teamLabel}`
                              : (myStatus
                                  ? 'Tap to change your reply'
                                  : 'Your coach is waiting on your reply')}
                          </span>
                        </span>
                        <span style={{ color: v.color, opacity: 0.6 }}>›</span>
                      </button>
                    );
                  })()}

                  {/* ── 2026-07-25 (per Gilles) — MY STATS card (players
                      only, right at the top under the hero). The fun
                      stats: W-L record, GF·GA, +/-, games played, win
                      rate — everything the coach's leaderboard shows
                      about them, on their own dashboard. Each mini-stat
                      honors the Profile → My Stats Tiles toggles
                      (statsTilePrefs, mig 107); the whole card hides if
                      every stat is toggled off or the player has no
                      games yet. Tapping goes to the full My Stats page. */}
                  {!isCoachRole && (() => {
                    const me = dashLeaderboard.find(p => p.id === user?.id);
                    if (!me) return null;
                    const prefs = user?.statsTilePrefs || {};
                    const show = (k) => prefs[k] !== false;
                    const played = me.gamesPlayed || 0;
                    const decided = (me.wins || 0) + (me.losses || 0) + (me.ties || 0);
                    if (played === 0 && (me.gamesIn || 0) === 0) return null;
                    const pm = me.plusMinus || 0;
                    // 2026-07-27 (per Gilles) — default order: Win %
                    // first, +/- second, Played third, then the rest.
                    const cells = [
                      show('winRate') && {
                        label: 'Win %',
                        value: decided > 0 ? `${Math.round(((me.wins || 0) / decided) * 100)}%` : '—',
                        color: 'var(--goal)',
                      },
                      show('plusMinus') && {
                        label: '+/−',
                        value: `${pm > 0 ? '+' : ''}${pm}`,
                        color: pm > 0 ? 'var(--goal)' : pm < 0 ? 'var(--penalty)' : 'var(--text-2)',
                      },
                      show('gamesPlayed') && { label: 'Played', value: played, color: 'var(--ice)' },
                      show('record') && {
                        label: (me.ties || 0) > 0 ? 'W-L-T' : 'W-L',
                        value: `${me.wins || 0}-${me.losses || 0}${(me.ties || 0) > 0 ? `-${me.ties}` : ''}`,
                        color: 'var(--puck)',
                      },
                      show('goals') && {
                        label: 'GF·GA',
                        value: `${me.goalsFor || 0}·${me.goalsAgainst || 0}`,
                        color: 'var(--text)',
                      },
                    ].filter(Boolean);
                    if (cells.length === 0) return null;
                    return (
                      <button
                        type="button"
                        onClick={() => navigate(`/team/${activeTeam.id}/my-stats`)}
                        className="card w-full text-left mb-3"
                        style={{
                          padding: '14px 16px',
                          borderRadius: 12,
                          cursor: 'pointer',
                          display: 'block',
                          WebkitTapHighlightColor: 'transparent',
                        }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                          <span style={{ fontSize: 15 }}>📊</span>
                          <span style={{
                            fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 12,
                            letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-2)',
                          }}>My Stats</span>
                          <span style={{ marginLeft: 'auto', color: 'var(--text-3)', fontSize: 12 }}>
                            Customize in Profile ›
                          </span>
                        </div>
                        <div style={{
                          display: 'flex', gap: 8, flexWrap: 'wrap',
                          justifyContent: 'space-between',
                        }}>
                          {cells.map(c => (
                            <div key={c.label} style={{ textAlign: 'center', flex: '1 1 0', minWidth: 56 }}>
                              <div style={{
                                fontFamily: 'var(--font-display)', fontWeight: 800,
                                fontSize: 18, lineHeight: 1.1, color: c.color,
                              }}>{c.value}</div>
                              <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                {c.label}
                              </div>
                            </div>
                          ))}
                        </div>
                      </button>
                    );
                  })()}

                  {/* ── Upcoming Reminders (coach-only) ──
                      Apr 2026 / Tom_Notes A7 — lives directly under the
                      Next Game hero. The card is collapsible internally
                      (first 2 events shown by default). */}
                  {isCoachRole && scheduledReminders && scheduledReminders.length > 0 && (
                    <UpcomingRemindersCard
                      reminders={scheduledReminders}
                      onGameClick={(gid) => navigate(`/game/${gid}`)}
                    />
                  )}
                </>
              );
          })()}

          {/* Apr 30 2026 (App_Change_List #4) — Your Teams quick
              switcher removed. Coach feedback: with multi-team users
              the dashboard had a chip row of every team they were on,
              eating vertical space above the actual game content. The
              same switching is available in the sidebar/drawer team
              picker, and Batch I will add a mobile header switcher
              for one-tap cycling. Until then, multi-team users use
              the drawer/sidebar — the dashboard no longer duplicates
              that affordance. */}

          {/* ── Drinks Duty Quick-Tile (Apr 2026) ──
              Surfaces the next-game drinks status on the dashboard so
              players don't need to drill into a game to volunteer or
              step down. Hidden when the team has drinks disabled or
              when there's no upcoming game. */}
          {upcomingGames[0] && (
            <DrinksDashboardTile
              teamId={activeTeam.id}
              game={upcomingGames[0]}
              userId={user?.id}
            />
          )}

          <div className="grid gap-5">
            {/* Upcoming + RSVP */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="section-title text-lg">Upcoming Games</h2>
                <button onClick={() => navigate(`/team/${activeTeam.id}/games`)} className="text-xs text-secondary hover:text-ice transition-colors flex items-center gap-1">All <ChevronRight size={12} /></button>
              </div>

              {loading ? (
                <div className="space-y-3">{[1,2,3].map(i=><div key={i} className="skeleton h-24 rounded-2xl"/>)}</div>
              ) : upcomingGames.length === 0 ? (
                (() => {
                  // Role-aware empty state (Apr 2026) — coaches/managers
                  // get a "Schedule a Game" CTA, players get a wait-for-
                  // your-coach message. Prevents the same confusion as
                  // the dashboard-level empty state: a player arriving
                  // on an empty team shouldn't be prompted to take
                  // coach-only actions.
                  const isCoachRole = ['coach','manager'].includes(activeTeam.member_role);
                  return (
                    <div className="zen-card p-10 text-center">
                      <Calendar size={36} className="mx-auto mb-3 opacity-20" style={{ color:'var(--ice)' }} />
                      <p style={{ color:'var(--text-secondary)' }}>No upcoming games</p>
                      {isCoachRole ? (
                        <button onClick={() => navigate(`/team/${activeTeam.id}/games?new=1`)} className="btn-ice mt-4 mx-auto"><Plus size={14} /> Schedule a Game</button>
                      ) : (
                        <p style={{ color:'var(--text-3)', fontSize: 12, marginTop: 8 }}>
                          Your coach hasn't scheduled any games yet. You'll be notified when they do.
                        </p>
                      )}
                    </div>
                  );
                })()
              ) : (
                <div className="space-y-3">
                  {upcomingGames.map((game, idx) => {
                    // Detect arena change vs the previous upcoming game
                    const prevArena = idx > 0 ? (upcomingGames[idx - 1].arena_name || '') : null;
                    const thisArena = game.arena_name || '';
                    const arenaChanged = prevArena !== null && thisArena !== prevArena && thisArena !== '';

                    return (
                    <div key={game.id} className="zen-card overflow-hidden active:scale-[0.99]"
                      style={{ transition: 'transform 0.1s', WebkitTapHighlightColor: 'transparent' }}>
                      {/* Arena change banner */}
                      {arenaChanged && (
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '7px 14px',
                          background: 'rgba(255,149,0,0.12)',
                          borderBottom: '1px solid rgba(255,149,0,0.25)',
                        }}>
                          <AlertTriangle size={13} style={{ color: 'var(--puck)', flexShrink: 0 }} />
                          <span style={{
                            fontSize: 11, fontWeight: 700, color: 'var(--puck)',
                            fontFamily: 'var(--font-display)', letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                          }}>
                            Different arena
                          </span>
                          <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginLeft: 2 }}>
                            — was <em style={{ color: 'var(--text)' }}>{prevArena || 'none'}</em>
                          </span>
                        </div>
                      )}
                      <button onClick={() => navigate(`/game/${game.id}`)} className="w-full text-left p-3 transition-colors"
                        style={{ WebkitTapHighlightColor: 'transparent' }}>
                        <div className="flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                              <span className="text-xs font-semibold flex-shrink-0">{getGameLabel(game.game_date)}</span>
                              {game.my_status && <span className={`badge ${sColors[game.my_status]} flex-shrink-0`}>{game.my_status === 'in' ? '✅' : game.my_status === 'out' ? '❌' : '⏳'} {game.my_status}</span>}
                            </div>
                            <div className="text-sm font-medium leading-snug">
                              {format(new Date(game.game_date.slice(0,10)+'T12:00:00'),'EEE, MMM d')} · {formatTime(game.game_time)}
                              {/* Apr 30 2026 (App_Change_List #18) — end
                                  time + duration. Subdued styling so it
                                  doesn't compete with date/time. */}
                              {(() => {
                                const durMin = game.duration_minutes
                                  || activeTeam?.settings?.default_game_duration_minutes;
                                const dur     = formatDuration(durMin);
                                const endTime = computeEndTime(game.game_time, durMin);
                                return (
                                  <>
                                    {endTime && <span style={{ opacity: 0.65, fontWeight: 400 }}> – {formatTime(endTime)}</span>}
                                    {dur && <span style={{ opacity: 0.65, fontWeight: 400 }}> · {dur}</span>}
                                  </>
                                );
                              })()}
                            </div>
                            {game.arena_name && (
                              <div className="text-xs flex items-center gap-1 mt-0.5 min-w-0"
                                style={{ color: arenaChanged ? 'var(--puck)' : 'var(--text-secondary)' }}>
                                <MapPin size={10} style={{ color: arenaChanged ? 'var(--puck)' : undefined, flexShrink: 0 }} />
                                <span className="truncate">{game.arena_name}</span>
                              </div>
                            )}
                          </div>
                          <div className="text-right flex-shrink-0">
                            {/* RSVP scoreboard — 4 quick-glance counts. Maybe was
                                removed app-wide; RSVP is binary in/out plus
                                the waitlist + no-response buckets. */}
                            <div className="flex items-center gap-2">
                              {(() => {
                                // Chunk 1 (Apr 2026): the "In" tile
                                // shows skaters only, with a small
                                // goalie count appended when > 0.
                                // Coach feedback: a team with 18
                                // skaters + 0 goalies is VERY
                                // different from 16 skaters + 2
                                // goalies, but the old "18 IN" pill
                                // looked the same in both cases.
                                const goaliesIn = parseInt(game.goalies_in) || 0;
                                const skatersIn = Math.max(0, (parseInt(game.players_in) || 0) - goaliesIn);
                                return (
                                  <div className="flex flex-col items-center" style={{ minWidth: 28 }}>
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                                      <span style={{ fontSize: 18, fontWeight: 800, lineHeight: 1, color: 'var(--goal)', fontFamily: 'var(--font-display)' }}>
                                        {skatersIn}
                                      </span>
                                      {goaliesIn > 0 && (
                                        <span style={{ fontSize: 11, fontWeight: 700, lineHeight: 1, color: 'var(--goal)', opacity: 0.85 }}>
                                          · 🥅 {goaliesIn}
                                        </span>
                                      )}
                                    </div>
                                    <span style={{ fontSize: 8, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 1 }}>
                                      In
                                    </span>
                                  </div>
                                );
                              })()}
                              {[
                                { val: game.players_out      || 0, color: 'var(--penalty)', label: 'Out' },
                                { val: game.players_waitlist || 0, color: '#a78bfa',        label: 'Wait List' },
                              ].map(({ val, color, label }) => (
                                <div key={label} className="flex flex-col items-center" style={{ minWidth: 24 }}>
                                  <span style={{ fontSize: 18, fontWeight: 800, lineHeight: 1, color, fontFamily: 'var(--font-display)' }}>
                                    {val}
                                  </span>
                                  <span style={{ fontSize: 8, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 1 }}>
                                    {label}
                                  </span>
                                </div>
                              ))}
                              {/* No response */}
                              {(() => {
                                const responded = (game.players_in || 0) + (game.players_out || 0) + (game.players_waitlist || 0);
                                // v2.5.147 — invited_count not total_members.
                                // Nullish-aware fallback.
                                const total = game.invited_count != null
                                                ? (parseInt(game.invited_count) || 0)
                                                : (parseInt(game.total_members) || 0);
                                const noResp = Math.max(0, total - responded);
                                return (
                                  <div className="flex flex-col items-center" style={{ minWidth: 24 }}>
                                    <span style={{ fontSize: 18, fontWeight: 800, lineHeight: 1, color: 'var(--text-2)', fontFamily: 'var(--font-display)' }}>
                                      {noResp}
                                    </span>
                                    <span style={{ fontSize: 8, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 1 }}>
                                      —
                                    </span>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      </button>

                      {/* RSVP strip
                          May 2 2026 — when the user is on the waitlist
                          (or pending_payment), show a read-only badge
                          instead of in/out buttons. Same fix pattern as
                          the today-card above; tapping I'M IN here used
                          to silently reset waitlist → in. */}
                      {(game.my_status === 'waitlist' || game.my_status === 'pending_payment') ? (
                        <div className="border-t flex items-center justify-center gap-2 py-2.5"
                          style={{
                            borderColor: 'rgba(255,255,255,0.06)',
                            background: 'rgba(167,139,250,0.08)',
                            color: '#a78bfa',
                            fontSize: 12, fontWeight: 700,
                            fontFamily: 'var(--font-display)',
                            letterSpacing: '0.06em',
                          }}>
                          ⏳ {game.my_status === 'pending_payment' ? 'Awaiting Payment' : 'On Waitlist'}
                          <span style={{ fontSize: 10, color: 'var(--text-2)', fontWeight: 500, letterSpacing: 0 }}>
                            · open game to manage
                          </span>
                        </div>
                      ) : (
                        <div className="border-t flex" style={{ borderColor:'rgba(255,255,255,0.06)' }}>
                          {[
                            { s:'in',  icon:CheckCircle, label:'In',  bg:'rgba(0,255,136,0.15)', fg:'var(--goal)' },
                            { s:'out', icon:XCircle,     label:'Out', bg:'rgba(255,68,68,0.15)',  fg:'var(--penalty)' },
                          ].map(({ s, icon:Icon, label, bg, fg }, idx) => {
                            const active = game.my_status === s;
                            return (
                              <button key={s} onClick={(e) => rsvp(e, game.id, s)}
                                disabled={!!rsvping[game.id]}
                                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-all active:opacity-70"
                                style={{
                                  background: active ? bg : 'transparent',
                                  color: active ? fg : 'var(--text-secondary)',
                                  borderRight: idx < 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                                  WebkitTapHighlightColor: 'transparent',
                                }}>
                                {rsvping[game.id] ? <div className="spinner" style={{width:12,height:12}}/> : <Icon size={13}/>}
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    ); // end return(
                  })}
                </div>
              )}
            </div>

            {/* Right col */}
            <div className="space-y-5">
              {/* Recent results */}
              {recentGames.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="section-title text-lg">Recent Results</h2>
                    <button onClick={() => navigate(`/team/${activeTeam.id}/games`)} className="text-xs text-secondary hover:text-ice transition-colors flex items-center gap-1">All <ChevronRight size={12}/></button>
                  </div>
                  <div className="space-y-2">
                    {recentGames.map(g => (
                      // v2.5.97 — coach feedback: "From the dashboard
                      // page there's a bug where when I click on a
                      // score of recent results, it takes me into the
                      // game instead of taking me to the actual
                      // scoreboard. Show me all the mini game scores."
                      // Replaced the single-button row with a div that
                      // has two distinct hit zones: the metadata area
                      // navigates to the game page (existing behavior),
                      // the score area navigates to the standalone
                      // scoreboard page where the v2.5.92 session
                      // strip lets the user browse all mini-game
                      // scores. The standalone scoreboard's Back
                      // button on mobile takes them to the game page
                      // (handled in v2.5.97 via navigate(-1) — see
                      // GameScoreboardPage's Back button).
                      <div key={g.id}
                        className="w-full flex items-center gap-3"
                        style={{
                          background: 'var(--surface)',
                          border: '1px solid var(--border)',
                          borderRadius: 12,
                          padding: '12px 14px',
                          transition: 'transform 0.1s, opacity 0.1s',
                        }}>
                        <button
                          type="button"
                          onClick={() => navigate(`/game/${g.id}`)}
                          className="flex items-center gap-3 flex-1 min-w-0 text-left active:scale-[0.98] active:opacity-80"
                          style={{
                            background: 'transparent', border: 'none', padding: 0,
                            WebkitTapHighlightColor: 'transparent',
                          }}>
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-sm" style={{ background:'rgba(0,255,136,0.1)', border:'1px solid rgba(0,255,136,0.2)' }}>🏟</div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-secondary">{format(parseISO(g.game_date),'MMM d, yyyy')}</div>
                            <div className="text-sm font-medium truncate">{g.arena_name || 'Game'}</div>
                          </div>
                        </button>
                        {g.result_home_score != null ? (
                          <button
                            type="button"
                            onClick={() => navigate(`/team/${activeTeam.id}/games/${g.id}/scoreboard`)}
                            className="flex-shrink-0 active:scale-[0.95]"
                            title="View scoreboard with all mini-game scores"
                            style={{
                              background: 'transparent', border: 'none',
                              padding: '4px 8px', borderRadius: 8,
                              cursor: 'pointer',
                              WebkitTapHighlightColor: 'transparent',
                            }}>
                            <div className="text-right">
                              <div className="font-bold font-mono text-lg leading-none" style={{color:'var(--goal)'}}>{g.result_home_score} — {g.result_away_score}</div>
                              <div className="text-xs text-secondary mt-0.5">scoreboard →</div>
                            </div>
                          </button>
                        ) : (
                          <span className="badge status-pending text-xs">No score</span>
                        )}
                        <button
                          type="button"
                          onClick={() => navigate(`/game/${g.id}`)}
                          aria-label="Open game"
                          style={{
                            background: 'transparent', border: 'none',
                            padding: 4, cursor: 'pointer',
                            WebkitTapHighlightColor: 'transparent',
                          }}>
                          <ChevronRight size={14} className="text-secondary"/>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 2026-07-10 (batch-B B1, per Gilles) — the full month-
                  grid MiniCalendar was REMOVED from the dashboard
                  entirely; the Schedule page's calendar view is the one
                  month grid now. The MiniCalendar component definition,
                  copyDashboardIcal, allGames/calDrinksMap state etc.
                  remain as dead code per house rule. */}

              {/* Apr 30 2026 (App_Change_List #8) — Quick Actions
                  section removed. Was a 5-button list (Manage Players /
                  Schedule Game / Team Chat / Team Stats / Message
                  Players) duplicating the sidebar nav. Coach feedback:
                  the section ate dashboard real estate without adding
                  navigation that wasn't already in the drawer.
                  Only Message Players is preserved here as a single
                  primary action — it's the ONE coach action that
                  doesn't have a clear nav home and benefits from
                  one-tap dashboard access. The rest live in the
                  sidebar/drawer where they always have. */}
              {['coach','manager'].includes(activeTeam.member_role) && (
                <button
                  onClick={openMessagePlayers}
                  className="w-full text-left flex items-center gap-3 active:scale-[0.98] active:opacity-80"
                  style={{
                    background: 'var(--surface)',
                    border: '1px solid var(--ice-a30)',
                    borderRadius: 12,
                    padding: '12px 14px',
                    transition: 'transform 0.1s, opacity 0.1s',
                    WebkitTapHighlightColor: 'transparent',
                  }}>
                  <div className="flex items-center justify-center flex-shrink-0"
                    style={{
                      width: 38, height: 38, borderRadius: 10,
                      background: 'var(--ice-a12)',
                      border: '1px solid var(--ice-a30)',
                    }}>
                    <Mail size={17} style={{ color: 'var(--ice)' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm" style={{ color: 'var(--text)' }}>
                      Message Players
                    </div>
                    <div className="text-xs" style={{ color: 'var(--text-2)' }}>
                      Email, push, or in-app message to your team
                    </div>
                  </div>
                  <ChevronRight size={15} style={{ color: 'var(--text-3)', flexShrink: 0 }}/>
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* ─── Wizard ─────────────────────────────────────────────── */}
      {/* ── Email Players modal (Dashboard — group-only, no picker) ───
          Lighter variant of the Players page modal: no per-player picker,
          just group filters. Quick broadcast from the Dashboard. ── */}
      {msgOpen && activeTeam && (() => {
        // v2.5.225 — resolve the selected audience to a concrete recipient
        // list so we can show "X recipients" + names BEFORE sending (parity
        // with the game-page Message box). Preset predicates mirror the
        // backend group SQL (routes/messages.js) so the preview matches what
        // actually goes out. Only real users (with a userId) are messageable.
        const roster = (msgRoster || []).filter(m => m.userId && !m.isDeleted);
        // NOTE: GET /teams/:id/members returns the role under `role`
        // (= member_role) — not `memberRole`. Position/isPrimary fields
        // are `position` / `isPrimary`. These predicates mirror the
        // backend group SQL in routes/messages.js exactly.
        const isStaff  = m => m.role === 'coach' || m.role === 'manager';
        const isGoalie = m => m.position === 'goalie';
        const groupPred = {
          all:       () => true,
          full_time: m => m.isPrimary === true  && m.role === 'player' && !isGoalie(m),
          spares:    m => m.isPrimary === false && !isStaff(m) && !isGoalie(m),
          goalies:   m => isGoalie(m),
          coaches:   m => isStaff(m),
        };
        let candidates;
        if (msgGroup === 'specific') {
          candidates = roster.filter(m => msgPicked.has(m.userId));
        } else {
          // Group sends exclude the sender (matches the backend) so the coach
          // doesn't email themselves their own blast.
          candidates = roster.filter(groupPred[msgGroup] || (() => true))
                             .filter(m => m.userId !== user?.id);
        }
        const targets = candidates.map(m => ({ userId: m.userId, name: m.name || 'Player' }));
        return (
        <>
        <div className="fixed left-0 right-0 top-0 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ zIndex: 500, height: 'var(--mobile-h, 100dvh)', top: msgVvTop /* 2026-09-16 keyboard-safe */ }}
          onClick={e => { if (e.target === e.currentTarget && !msgSending) setMsgOpen(false); }}>
          <div ref={msgSheetRef} className="glass w-full sm:max-w-lg animate-slide-up"
            style={{
              // v2.5.226 — size to --mobile-h (visualViewport height, set live
              // by MobileLayout) instead of dvh, so the sheet shrinks above the
              // iOS keyboard and the Send button stays reachable. The overlay
              // drops the bottom anchor (left/right/top + height) for the same
              // reason. Mirrors the game-page Modal fix (v2.5.86).
              maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.85)', overflowY: 'auto', WebkitOverflowScrolling: 'touch',
              borderRadius: '16px 16px 0 0',
              padding: '24px 16px calc(env(safe-area-inset-bottom, 0px) + 16px)',
              position: 'relative',
            }}>
            <ModalCloseButton onClose={() => setMsgOpen(false)} disabled={msgSending} />
            <h2 className="section-title text-2xl mb-1" style={{ paddingRight: 44 }}>Message Players</h2>
            <p className="text-xs text-secondary mb-4" style={{ lineHeight: 1.5 }}>
              Send a message to your team via email, push notification, or in-app.
              Replies to email come directly to your inbox.
            </p>

            <label className="label">Send to</label>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {[
                { key: 'all',       label: 'Everyone',     emoji: '👥', desc: 'Full roster' },
                { key: 'full_time', label: 'Full-Time',    emoji: '👤', desc: 'FT skaters' },
                { key: 'spares',    label: 'Spares',       emoji: '🔄', desc: 'Spare skaters' },
                { key: 'goalies',   label: 'Goalies',      emoji: '🥅', desc: 'All goalies' },
                { key: 'coaches',   label: 'Staff',        emoji: '🛡', desc: 'Coaches & managers' },
                { key: 'specific',  label: 'Pick Players', emoji: '🎯', desc: 'Search & select', span: true },
              ].map(opt => {
                const active = msgGroup === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setMsgGroup(opt.key)}
                    disabled={msgSending}
                    className="flex flex-col items-start gap-0.5 py-2 px-3 rounded-lg text-xs font-semibold transition-all text-left"
                    style={{
                      background: active ? 'var(--ice-a12)' : 'var(--surface-2)',
                      border: `1.5px solid ${active ? 'var(--ice-a30)' : 'var(--border)'}`,
                      color: active ? 'var(--ice)' : 'var(--text-2)',
                      cursor: msgSending ? 'not-allowed' : 'pointer',
                      gridColumn: opt.span ? '1 / -1' : undefined,
                    }}>
                    <span><span style={{ marginRight: 4 }}>{opt.emoji}</span>{opt.label}</span>
                    <span className="text-xs" style={{ color: 'var(--text-3)', fontWeight: 500 }}>{opt.desc}</span>
                  </button>
                );
              })}
            </div>

            {/* v2.5.225 — Pick Players picker (visible only when scope = 'specific'). */}
            {msgGroup === 'specific' && (
              <div className="mb-3">
                <input
                  type="text"
                  value={msgSearch}
                  onChange={e => setMsgSearch(e.target.value)}
                  placeholder="🔍 Search by name…"
                  className="input-field mb-2"
                  disabled={msgSending}
                  style={{ fontSize: 13 }}
                />
                {(() => {
                  const q = msgSearch.trim().toLowerCase();
                  const list = roster.filter(m => !q || (m.name || '').toLowerCase().includes(q));
                  const pickedCount = msgPicked.size;
                  return (
                    <>
                      <div className="flex items-center justify-between text-xs mb-2 px-1">
                        <span style={{ color: 'var(--text-2)' }}>
                          {msgRosterLoading
                            ? 'Loading roster…'
                            : pickedCount === 0 ? 'Tap players to add them' : `${pickedCount} selected`}
                        </span>
                        {pickedCount > 0 && (
                          <button
                            type="button"
                            onClick={() => setMsgPicked(new Set())}
                            disabled={msgSending}
                            className="text-xs"
                            style={{ color: 'var(--ice)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', fontWeight: 600 }}>
                            Clear all
                          </button>
                        )}
                      </div>
                      <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface-2)' }}>
                        {list.length === 0 ? (
                          <div className="text-xs text-center p-4" style={{ color: 'var(--text-3)' }}>
                            {msgRosterLoading ? 'Loading…' : `No players match "${q}"`}
                          </div>
                        ) : (
                          list.map((m, idx) => {
                            const isPicked = msgPicked.has(m.userId);
                            return (
                              <div
                                key={m.userId || m.inviteId || idx}
                                onClick={() => {
                                  if (msgSending) return;
                                  setMsgPicked(prev => {
                                    const next = new Set(prev);
                                    if (next.has(m.userId)) next.delete(m.userId);
                                    else next.add(m.userId);
                                    return next;
                                  });
                                }}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
                                  borderBottom: idx < list.length - 1 ? '1px solid var(--border)' : 'none',
                                  cursor: msgSending ? 'not-allowed' : 'pointer',
                                  background: isPicked ? 'var(--ice-a8)' : 'transparent',
                                  WebkitTapHighlightColor: 'transparent',
                                }}>
                                <input type="checkbox" checked={isPicked} readOnly style={{ accentColor: 'var(--ice)', pointerEvents: 'none' }} />
                                <span className="text-sm" style={{ color: isPicked ? 'var(--ice)' : 'var(--text)', fontWeight: isPicked ? 600 : 400 }}>
                                  {m.name || 'Player'}
                                </span>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {/* v2.5.225 — recipient count + name preview (parity with the game box).
                v2.5.226 — the whole chip is tappable to open the full names list. */}
            <div
              role={targets.length > 0 ? 'button' : undefined}
              tabIndex={targets.length > 0 ? 0 : undefined}
              onClick={() => { if (targets.length > 0) setMsgRecipientsOpen(true); }}
              className="text-xs mb-4 px-3 py-2 rounded-lg"
              style={{
                background: targets.length > 0 ? 'var(--ice-a5)' : 'rgba(255,149,0,0.08)',
                border: `1px solid ${targets.length > 0 ? 'var(--ice-a15)' : 'rgba(255,149,0,0.2)'}`,
                color: targets.length > 0 ? 'var(--ice)' : 'var(--puck)',
                cursor: targets.length > 0 ? 'pointer' : 'default',
              }}>
              {msgRosterLoading ? (
                'Loading recipients…'
              ) : targets.length > 0 ? (
                <>
                  <div className="font-semibold mb-1 flex items-center justify-between gap-2">
                    <span>✉ {targets.length} recipient{targets.length !== 1 ? 's' : ''}</span>
                    <span style={{ fontWeight: 600 }}>View all →</span>
                  </div>
                  <div style={{ color: 'var(--text-2)', lineHeight: 1.5, fontWeight: 400 }}>
                    {targets.slice(0, 6).map(t => t.name).join(', ')}
                    {targets.length > 6 && ` + ${targets.length - 6} more`}
                  </div>
                </>
              ) : (
                msgGroup === 'specific' ? '⚠ No players selected — pick at least one' : '⚠ No players match — pick a different group'
              )}
            </div>

            <label className="label">Subject</label>
            <input
              type="text"
              value={msgSubject}
              onChange={e => setMsgSubject(e.target.value)}
              maxLength={200}
              placeholder="Subject line"
              className="input-field mb-3"
              disabled={msgSending}
            />

            <label className="label">Message</label>
            <textarea
              value={msgBody}
              onChange={e => setMsgBody(e.target.value)}
              maxLength={4000}
              rows={8}
              placeholder="Type your message here…"
              className="input-field mb-1"
              style={{ resize: 'vertical', minHeight: 130, fontFamily: 'inherit', lineHeight: 1.5 }}
              disabled={msgSending}
            />
            <div className="text-xs text-right mb-4" style={{ color: 'var(--text-3)', fontSize: 10 }}>
              {msgBody.length}/4000
            </div>

            {/* ── Channel selector (Apr 2026, Tom_Notes B8) ── */}
            <label className="label">Send via</label>
            {/* 2026-09-17 (Phase 4) — shared picker (Player preferences /
                Email / Push / SMS); see components/shared/MessageChannelPicker. */}
            <MessageChannelPicker
              value={msgChannels}
              onChange={setMsgChannels}
              smsEnabled={activeTeam?.sms_enabled === true}
              disabled={msgSending}
            />

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => { if (!msgSending) setMsgOpen(false); }}
                className="btn-ghost flex-1 justify-center"
                disabled={msgSending}>
                Cancel
              </button>
              <button
                type="button"
                onClick={sendMessagePlayers}
                disabled={msgSending || !msgSubject.trim() || !msgBody.trim() || targets.length === 0}
                className="btn-ice flex-1 justify-center">
                {msgSending
                  ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Sending…</>
                  : <><Mail size={14} /> Send{targets.length > 0 ? ` to ${targets.length}` : ''}</>}
              </button>
            </div>
            <div className="sm:hidden" aria-hidden style={{ height: 70 }} />
          </div>
        </div>

        {/* v2.5.226 — full recipient list popup. Rendered as a SIBLING overlay
            (not nested in the message card) because that card uses a transform
            via animate-slide-up, which would trap a nested position:fixed
            overlay. Higher z-index (600) so it sits above the message box. */}
        {msgRecipientsOpen && (
          <div className="fixed left-0 right-0 top-0 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
            style={{ zIndex: 600, height: 'var(--mobile-h, 100dvh)' }}
            onClick={e => { if (e.target === e.currentTarget) setMsgRecipientsOpen(false); }}>
            <div className="glass w-full sm:max-w-sm animate-slide-up"
              style={{
                maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.85)', display: 'flex', flexDirection: 'column',
                borderRadius: '16px 16px 0 0',
                padding: '20px 16px calc(env(safe-area-inset-bottom, 0px) + 16px)',
                position: 'relative',
              }}>
              <ModalCloseButton onClose={() => setMsgRecipientsOpen(false)} />
              <h3 className="section-title text-xl mb-1" style={{ paddingRight: 44 }}>Recipients</h3>
              <p className="text-xs text-secondary mb-3" style={{ lineHeight: 1.5 }}>
                {targets.length} {targets.length === 1 ? 'person' : 'people'} will get this message.
              </p>
              <div style={{
                overflowY: 'auto', minHeight: 0,
                border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface-2)',
              }}>
                {targets.map((t, i) => (
                  <div key={t.userId || i}
                    style={{
                      padding: '9px 12px', fontSize: 14, color: 'var(--text)',
                      borderBottom: i < targets.length - 1 ? '1px solid var(--border)' : 'none',
                    }}>
                    {t.name}
                  </div>
                ))}
              </div>
              <button type="button" onClick={() => setMsgRecipientsOpen(false)}
                className="btn-ghost w-full justify-center" style={{ marginTop: 12 }}>
                Close
              </button>
            </div>
          </div>
        )}
        </>
        );
      })()}

      {/* 2026-10-18 — type chooser: accounts with Press Box pick the kind of
          team first. Full team continues into the wizard below; Press Box
          goes to its own setup (no settings copied, no payments step). */}
      {showWizard && pressBoxVisible(user) && wizardType === null && (
        <div className="fixed inset-0 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ zIndex: 500 }}>
          <div className="w-full sm:max-w-lg animate-slide-up"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '16px 16px 0 0',
              padding: '20px 16px calc(env(safe-area-inset-bottom, 0px) + 16px)' }}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold">What kind of team?</h2>
              <button type="button" onClick={closeWizard} aria-label="Cancel"
                style={{ appearance: 'none', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8,
                  color: 'var(--text-2)', width: 30, height: 30, display: 'inline-flex', alignItems: 'center',
                  justifyContent: 'center', cursor: 'pointer', fontSize: 15, fontWeight: 700 }}>✕</button>
            </div>
            <button type="button" onClick={() => setWizardType('full')}
              className="w-full text-left mb-3"
              style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 12, padding: 14, cursor: 'pointer' }}>
              <div className="flex items-center gap-2 font-bold mb-1" style={{ color: 'var(--text)' }}><Users size={16} style={{ color: 'var(--ice)' }} /> Full team</div>
              <div className="text-sm" style={{ color: 'var(--text-2)' }}>
                Everything: players with accounts, schedule, chat, availability, payments, scoreboards.
              </div>
            </button>
            <button type="button" onClick={() => { setShowWizard(false); setWizardType(null); navigate('/press-box/setup'); }}
              className="w-full text-left"
              style={{ background: 'var(--surface-2)', border: '1px solid var(--gold)', borderRadius: 12, padding: 14, cursor: 'pointer' }}>
              <div className="flex items-center gap-2 font-bold mb-1" style={{ color: 'var(--text)' }}><Radio size={16} style={{ color: 'var(--gold)' }} /> {PRESS_BOX.NAME} team</div>
              <div className="text-sm" style={{ color: 'var(--text-2)' }}>
                Stats only: record or upload games, the AI writes the play-by-play and the stats, and you share a public
                stats page. Jersey numbers instead of player accounts — nothing to pay, nobody to invite unless you want help recording.
              </div>
            </button>
          </div>
        </div>
      )}

      {showWizard && !(pressBoxVisible(user) && wizardType === null) && (
        // May 1 2026 — was `z-50` (z-index: 50). The mobile tab bar
        // is `position: fixed; z-index: 400` and was sitting OVER
        // the modal, cutting off the Cancel/Next buttons at the
        // bottom on phones. Bumped to z-index 500 so the modal
        // floats above every layout chrome element. The tab bar
        // remains accessible — once the user closes the modal,
        // it's back in front.
        <div className="fixed inset-0 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ zIndex: 500 }}>
          <div className="w-full sm:max-w-lg animate-slide-up"
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: '16px 16px 0 0',
              padding: '20px 16px calc(env(safe-area-inset-bottom, 0px) + 16px)',
              maxHeight: '90dvh',
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
            }}>
            {/* 2026-07-10 (wizard v2, coach request) — ✕ cancels team
                creation. Steps 2-3 confirm + delete the already-created
                team; step 1 just closes; step 4 finishes (see wizardAbort). */}
            {/* 2026-07-14 (mobile pass) — sticky positioning so the ✕ stays
                reachable even after scrolling into a long step (step 5's
                checklist + menu glossary runs well past one screen on a
                phone). Previously the ✕ lived inline in the scrollable
                content and scrolled out of view with everything else,
                leaving no way to close the wizard without scrolling back
                up. `top: -20px` / `marginTop: -20px` cancel out the parent's
                20px top padding so the sticky bar sits flush against the
                very top of the sheet with no gap, and the opaque background
                keeps scrolled content from showing through underneath it. */}
            <div style={{
              display: 'flex', justifyContent: 'flex-end', marginBottom: -6,
              position: 'sticky', top: -20, marginTop: -20, paddingTop: 20,
              background: 'var(--surface)', zIndex: 2,
            }}>
              <button
                type="button"
                onClick={wizardAbort}
                aria-label="Cancel team creation"
                title="Cancel team creation"
                style={{
                  appearance: 'none', background: 'var(--surface-2)',
                  border: '1px solid var(--border)', borderRadius: 8,
                  color: 'var(--text-2)', width: 30, height: 30,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', fontSize: 15, lineHeight: 1, fontWeight: 700,
                }}>
                ✕
              </button>
            </div>
            <WizardDots />

            {/* Apr 30 2026 — Back button per step. Only visible on
                steps 2+ since step 1 is the first step (Cancel
                button at the bottom of step 1 dismisses the
                wizard entirely). Stepping BACK from step 4 returns
                to step 3 unconditionally; from step 3 → step 2;
                from step 2 → step 1. Resetting the inline review
                flag when leaving step 2 prevents the next visit
                from landing in the wrong sub-state. */}
            {/* Apr 30 2026 — back button made more prominent and
                explicitly hidden on the post-creation success
                screen (step 4). Coach feedback: previous design was
                a subtle text link that was easy to miss; bumped to
                a properly bordered chip with the team's progress
                step explicit ("← Back to step N") so the navigation
                affordance is unambiguous. Step 4 has no meaningful
                "back" since the team's already created — the
                wizard's only forward action is "Done / Close". */}
            {/* 2026-07-10 — Back now available on step 4 too (coach asked
                for back on all steps). Returning is safe everywhere since
                step-1 re-submit is EDIT MODE (never re-creates the team). */}
            {wizardStep > 1 && (
              <div style={{ marginTop: -4, marginBottom: 12 }}>
                <button
                  type="button"
                  onClick={() => {
                    if (wizardStep === 3) {
                      // Going back from the SCHEDULE step (now step 3 —
                      // 2026-07-10 Phase 2.5 inserted Settings as step 2).
                      // Reset the file picker / extracted result / review
                      // flag so the user can pick a different file (or
                      // skip) on the next forward pass.
                      setWizardScheduleStatus('idle');
                      setWizardScheduleFile(null);
                      setWizardScheduleResult(null);
                      setWizardScheduleError(null);
                      setWizardReviewing(false);
                    }
                    setWizardStep(s => Math.max(1, s - 1));
                  }}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg"
                  style={{
                    background: 'var(--surface-2)',
                    border: '1px solid var(--border)',
                    color: 'var(--text-2)',
                    cursor: 'pointer',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    fontFamily: 'var(--font-display)',
                    textTransform: 'uppercase',
                    fontSize: 11,
                  }}>
                  <ArrowLeft size={12} /> Back to step {wizardStep - 1}
                </button>
              </div>
            )}

            {wizardStep === 1 && (
              <>
                <h2 className="section-title text-xl mb-1">Create Your Team</h2>
                <p className="text-secondary text-sm mb-5">Basics first — you can always change these later.</p>
                <form onSubmit={wizardCreateTeam} className="space-y-4">
                  <div>
                    <label className="label" title="Your team is the permanent thing — one roster of people that carries over from season to season.">Team Name *</label>
                    <input className="input-field" type="text" placeholder="Wednesday Summer Hockey" required value={wizardForm.name} autoComplete="organization" onChange={wf('name')} />
                    <p className="text-xs text-secondary mt-1" style={{ opacity: 0.7 }}>
                      The permanent group you manage — your roster carries over from season to season.
                    </p>
                  </div>
                  {/* 2026-07-10 (coach request) — League Name removed from
                      onboarding (still editable in Team Settings later).
                      First Season replaces it: captured at creation so
                      games + stats are scoped from day one. Backend
                      creates + activates the season and seeds
                      season_members (teams.js POST /). */}
                  <div>
                    <label className="label" title="A season is a chapter within your team — games and stats are grouped by season while the roster carries over.">First Season *</label>
                    <input className="input-field" type="text" placeholder="Summer 2026" required value={wizardForm.seasonName} onChange={wf('seasonName')} />
                    <p className="text-xs text-secondary mt-1" style={{ opacity: 0.7 }}>
                      A chapter within your team — “Summer 2026”, “Winter 2026/27” — keeping schedules and
                      stats separate per session while the roster carries over. You can add more seasons later.
                    </p>
                  </div>
                  {/* Apr 2026 — sport dropdown moved into the wizard
                      so coaches pick once at creation. Game day/time
                      removed (those come from the imported schedule).
                      The sport list mirrors the one in TeamPage so the
                      same emoji + labels show in the team header
                      after creation. */}
                  <div>
                    <label className="label">Sport</label>
                    {/* 2026-07-10 (coach decision, backlog Q1) — trimmed to
                        the sports folks actually use; the rest are a
                        disabled "coming soon" group. Mirrors TeamPage. */}
                    <select className="input-field" value={wizardForm.teamSport} onChange={wf('teamSport')}>
                      {[
                        { value: 'hockey',     emoji: '🏒', label: 'Ice Hockey' },
                        { value: 'ball-hockey',emoji: '🏑', label: 'Ball Hockey' },
                        { value: 'soccer',     emoji: '⚽', label: 'Soccer / Football' },
                        { value: 'baseball',   emoji: '⚾', label: 'Baseball' },
                        { value: 'softball',   emoji: '🥎', label: 'Softball' },
                        { value: 'basketball', emoji: '🏀', label: 'Basketball' },
                        { value: 'volleyball', emoji: '🏐', label: 'Volleyball' },
                        { value: 'lacrosse',   emoji: '🥍', label: 'Lacrosse' },
                        { value: 'other',      emoji: '🏅', label: 'Other' },
                      ].map(s => (
                        <option key={s.value} value={s.value}>{s.emoji} {s.label}</option>
                      ))}
                      <optgroup label="Coming soon (TBD)">
                        {[
                          { value: 'football',   emoji: '🏈', label: 'American Football' },
                          { value: 'tennis',     emoji: '🎾', label: 'Tennis' },
                          { value: 'badminton',  emoji: '🏸', label: 'Badminton' },
                          { value: 'curling',    emoji: '🥌', label: 'Curling' },
                          { value: 'rugby',      emoji: '🏉', label: 'Rugby' },
                          { value: 'cricket',    emoji: '🏏', label: 'Cricket' },
                        ].map(s => (
                          <option key={s.value} value={s.value} disabled>{s.emoji} {s.label}</option>
                        ))}
                      </optgroup>
                    </select>
                    <p className="text-xs text-secondary mt-1" style={{ opacity: 0.7 }}>
                      Pick the sport you'll be running. Used for icon styling — game-day workflow is the same regardless.
                    </p>
                  </div>
                  <div className="flex gap-3 pt-2">
                    {/* 2026-07-10 — Cancel shares the ✕ semantics: in edit
                        mode (backed here from step 2+) the team already
                        exists, so cancelling confirms + deletes it rather
                        than silently stranding a half-made team. */}
                    <button type="button" onClick={wizardAbort} className="btn-ghost flex-1 justify-center">Cancel</button>
                    <button type="submit" disabled={creating} className="btn-ice flex-1 justify-center">
                      {creating ? <div className="spinner"/> : <>Next <ArrowRight size={14}/></>}
                    </button>
                  </div>
                </form>
              </>
            )}

            {/* 2026-07-10 (Phase 2.5) — NEW step 2: settings inheritance.
                The team + season already exist (created in step 1), so
                "copy" applies the source team's saved settings bundle via
                POST /copy-settings (backend re-checks the coach is a
                coach/manager on the SOURCE team too — same guard as
                copy-members). "Keep defaults" is a no-op: creation
                already applied the platform defaults. */}
            {/* 2026-09-17 (Phase 6) — Stripe transfer sub-step (step 2b). */}
            {wizardStep === 2 && wizardStripe && (
              <>
                <h2 className="section-title text-xl mb-1">🏦 Stripe payments</h2>
                <p className="text-secondary text-sm mb-4" style={{ lineHeight: 1.5 }}>
                  You already have a Stripe account connected on{' '}
                  {wizardStripe.reusable.length === 1
                    ? <strong style={{ color: 'var(--text)' }}>{wizardStripe.reusable[0].teamName}</strong>
                    : 'another team'}.
                  Want to use it for <strong style={{ color: 'var(--text)' }}>{wizardTeam?.name}</strong> too?
                  Spare and player payments for this team would land in that same bank account.
                </p>
                <div className="space-y-2 mb-4">
                  {wizardStripe.reusable.map(src => (
                    <button key={src.teamId} type="button" disabled={wizardStripeBusy}
                      onClick={async () => {
                        setWizardStripeBusy(true);
                        try {
                          await api.post(`/teams/${wizardTeam.id}/stripe-connect/link`, { sourceTeamId: src.teamId });
                          toast.success(`Stripe account from ${src.teamName} linked to ${wizardTeam?.name} ✅`);
                          setWizardStripe(null);
                          setWizardStep(3);
                        } catch (err) {
                          toast.error(err.response?.data?.error || 'Could not link the Stripe account — you can do it later under Team Settings → Stripe');
                        }
                        setWizardStripeBusy(false);
                      }}
                      className="w-full text-left p-3 rounded-xl"
                      style={{ background: 'rgba(0,232,122,0.06)', border: '1px solid rgba(0,232,122,0.35)', cursor: 'pointer' }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--goal)' }}>
                        {wizardStripeBusy ? 'Linking…' : `✅ Yes — use my Stripe account from ${src.teamName}`}
                      </div>
                      <div className="text-xs text-secondary mt-0.5">
                        {src.accountId ? `${src.accountId}` : 'Connected account'}{src.country ? ` · ${src.country}` : ''} · charges enabled. Nothing to re-do on Stripe.
                      </div>
                    </button>
                  ))}
                  <button type="button" disabled={wizardStripeBusy}
                    onClick={() => { setWizardStripe(null); setWizardStep(3); }}
                    className="w-full text-left p-3 rounded-xl"
                    style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', cursor: 'pointer' }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>No — I'll set up Stripe separately</div>
                    <div className="text-xs text-secondary mt-0.5">
                      To collect payments on this team you'll need to connect a Stripe account later under Team Settings → Stripe. Until then, spares are invited for free.
                    </div>
                  </button>
                </div>
              </>
            )}
            {wizardStep === 2 && !wizardStripe && (
              <>
                <h2 className="section-title text-xl mb-1">Team Settings</h2>
                <p className="text-secondary text-sm mb-5">
                  Reminders, balancer, payments, drinks — start fresh or copy everything from a team you already run.
                </p>
                <div className="space-y-3">
                  {[
                    { v: 'keep', l: 'Start with the defaults', d: 'Sensible out-of-the-box settings — tune anything later in Team Settings.' },
                    { v: 'copy', l: 'Copy settings from another team', d: 'Bring over reminder times, balancer mode, fees, and the rest from one of your teams.' },
                  ].map(opt => {
                    const active = wizardSettingsMode === opt.v;
                    return (
                      <button key={opt.v} type="button"
                        onClick={() => setWizardSettingsMode(opt.v)}
                        className="w-full text-left p-3 rounded-xl"
                        style={{
                          background: active ? 'rgba(96,165,250,0.08)' : 'var(--surface-2)',
                          border: `1px solid ${active ? 'var(--ice)' : 'var(--border)'}`,
                          cursor: 'pointer',
                        }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: active ? 'var(--ice)' : 'var(--text)' }}>{opt.l}</div>
                        <div className="text-xs text-secondary mt-0.5">{opt.d}</div>
                      </button>
                    );
                  })}
                  {wizardSettingsMode === 'copy' && (() => {
                    // Same source filter as the copy-players step: the
                    // coach's OTHER coach/manager teams.
                    const sourceTeams = (teams || []).filter(t =>
                      ['coach', 'manager'].includes(t.member_role)
                      && t.id !== wizardTeam?.id
                    );
                    if (sourceTeams.length === 0) {
                      return (
                        <p className="text-xs text-secondary" style={{ opacity: 0.8 }}>
                          You don't coach or manage any other teams yet — continue with the defaults.
                        </p>
                      );
                    }
                    return (
                      <div>
                        <label className="label">Copy settings from</label>
                        <select className="input-field" value={wizardSettingsFromTeamId}
                          onChange={e => setWizardSettingsFromTeamId(e.target.value)}>
                          <option value="">— Choose a team —</option>
                          {sourceTeams.map(t => (
                            <option key={t.id} value={t.id}>
                              {t.name}{t.league_name ? ` · ${t.league_name}` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  })()}
                  <div className="flex gap-3 pt-2">
                    <button type="button" disabled={wizardApplyingSettings}
                      onClick={async () => {
                        if (wizardSettingsMode === 'copy' && wizardSettingsFromTeamId && wizardTeam) {
                          setWizardApplyingSettings(true);
                          try {
                            await api.post(`/teams/${wizardTeam.id}/copy-settings`, {
                              fromTeamId: wizardSettingsFromTeamId,
                            });
                            toast.success('Settings copied!');
                          } catch (err) {
                            toast.error(err.response?.data?.error || 'Could not copy settings');
                            setWizardApplyingSettings(false);
                            return; // stay on the step so the coach can retry or pick defaults
                          }
                          setWizardApplyingSettings(false);
                        } else if (wizardSettingsMode === 'copy' && !wizardSettingsFromTeamId) {
                          toast.error('Pick a team to copy from, or choose the defaults');
                          return;
                        }
                        // 2026-09-17 — Stripe transfer sub-step: if the coach has a
                        // finished Stripe account on another team, ask whether to
                        // carry it over before moving on. Best-effort: any error
                        // just skips the question.
                        try {
                          setWizardApplyingSettings(true);
                          const { data: st } = await api.get(`/teams/${wizardTeam.id}/stripe-connect/status`);
                          const reusable = Array.isArray(st?.reusable) ? st.reusable : [];
                          setWizardApplyingSettings(false);
                          if (reusable.length > 0 && st?.connected !== true) {
                            setWizardStripe({ reusable });
                            return; // stay on step 2, sub-screen renders below
                          }
                        } catch { setWizardApplyingSettings(false); }
                        setWizardStep(3); // → schedule step
                      }}
                      className="btn-ice flex-1 justify-center">
                      {wizardApplyingSettings ? <div className="spinner"/> : <>Continue <ArrowRight size={14}/></>}
                    </button>
                  </div>
                </div>
              </>
            )}

            {wizardStep === 3 && (
              <>
                <h2 className="section-title text-xl mb-1">Import Your Schedule</h2>
                <p className="text-secondary text-sm mb-5">
                  Upload a PDF, image, or screenshot of your league schedule. Our AI extracts each game's
                  date, time, and arena. You can review and tweak everything afterwards.
                </p>

                {/* Apr 2026 — wizard step 2 rebuilt. Replaces the old
                    "Home Arena" form (arena info now comes from the
                    schedule itself). The flow:
                      1. Coach picks a file
                      2. Click Extract — we POST to schedule-import
                      3. AI runs, returns a list of games
                      4. We show the count + offer to fine-tune via
                         the standalone import page, OR move on.
                    Skip is always available — the coach can come back
                    and import later from the Schedule tab. */}
                {wizardScheduleStatus === 'idle' && (
                  <div className="space-y-4">
                    <label className="block">
                      <input
                        type="file"
                        accept="application/pdf,image/jpeg,image/png,image/webp,image/gif"
                        onChange={e => {
                          const f = e.target.files?.[0] || null;
                          setWizardScheduleFile(f);
                          setWizardScheduleError(null);
                        }}
                        style={{ display: 'none' }} />
                      <div className="p-4 rounded-xl text-center cursor-pointer transition-all"
                        style={{
                          background: wizardScheduleFile ? 'rgba(0,212,255,0.06)' : 'var(--surface-2)',
                          border: `2px dashed ${wizardScheduleFile ? 'var(--ice)' : 'var(--border)'}`,
                        }}>
                        <Upload size={24} style={{ color: wizardScheduleFile ? 'var(--ice)' : 'var(--text-3)', margin: '0 auto 8px' }} />
                        {wizardScheduleFile ? (
                          <>
                            <p className="text-sm font-semibold" style={{ color: 'var(--ice)' }}>{wizardScheduleFile.name}</p>
                            <p className="text-xs text-secondary mt-1">{(wizardScheduleFile.size / 1024).toFixed(0)} KB · click to change</p>
                          </>
                        ) : (
                          <>
                            <p className="text-sm font-semibold">Tap to choose a schedule file</p>
                            <p className="text-xs text-secondary mt-1">PDF, PNG, or JPG. Up to ~10 MB.</p>
                          </>
                        )}
                      </div>
                    </label>
                    {wizardScheduleError && (
                      <p className="text-xs" style={{ color: 'var(--penalty)' }}>{wizardScheduleError}</p>
                    )}
                    {wizardScheduleFile && wizardScheduleFile.size > 10 * 1024 * 1024 && (
                      <p className="text-xs" style={{ color: 'var(--puck)' }}>
                        ⚠ This file is large — extraction may take 30–60 seconds.
                      </p>
                    )}
                    <div className="flex gap-3 pt-1">
                      <button type="button" onClick={() => setWizardStep(4)} className="btn-ghost flex-1 justify-center">Skip — I'll import later</button>
                      <button
                        type="button"
                        onClick={wizardUploadSchedule}
                        disabled={!wizardScheduleFile}
                        className="btn-ice flex-1 justify-center">
                        <Zap size={14}/> Extract Games
                      </button>
                    </div>
                  </div>
                )}

                {(wizardScheduleStatus === 'uploading' || wizardScheduleStatus === 'extracting') && (
                  <div className="p-6 rounded-xl text-center" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                    <div className="spinner" style={{ width: 32, height: 32, margin: '0 auto 12px' }} />
                    <p className="text-sm font-semibold">
                      {wizardScheduleStatus === 'uploading' ? 'Uploading…' : 'Reading your schedule…'}
                    </p>
                    <p className="text-xs text-secondary mt-1">This usually takes 5–15 seconds.</p>
                  </div>
                )}

                {wizardScheduleStatus === 'done' && wizardScheduleResult && !wizardReviewing && (
                  <div className="space-y-4">
                    <div className="p-4 rounded-xl text-center" style={{ background: 'rgba(0,232,122,0.08)', border: '1.5px solid rgba(0,232,122,0.4)' }}>
                      <CheckCircle size={28} style={{ color: 'var(--goal)', margin: '0 auto 8px' }} />
                      <p className="text-sm font-semibold" style={{ color: 'var(--goal)' }}>
                        Found {wizardScheduleResult.gamesCount} {wizardScheduleResult.gamesCount === 1 ? 'game' : 'games'}!
                      </p>
                      <p className="text-xs text-secondary mt-1" style={{ lineHeight: 1.4 }}>
                        Nothing's saved yet — review the games on the next screen and confirm, or
                        skip and import later from the Schedule tab.
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <button type="button" onClick={() => setWizardStep(4)} className="btn-ghost flex-1 justify-center">
                        Skip for now
                      </button>
                      {/* Apr 30 2026 — open the inline review (same
                          ReviewStep used on /games/import) instead of
                          navigating away. The coach can edit dates,
                          times, arenas, deselect rows, and confirm — all
                          without leaving the wizard. The extracted games
                          are passed as a prop. */}
                      <button type="button"
                        onClick={() => setWizardReviewing(true)}
                        className="btn-ice flex-1 justify-center">
                        <FileText size={14}/> Review &amp; save
                      </button>
                    </div>
                  </div>
                )}

                {/* Apr 30 2026 — inline ReviewStep. Renders inside the
                    wizard so the coach completes the schedule import
                    without losing context. onConfirm hits the same
                    /confirm endpoint as the standalone page; onReset
                    drops back to the upload step (in case the AI got
                    things badly wrong and they want to upload a
                    different file). */}
                {wizardScheduleStatus === 'done' && wizardScheduleResult && wizardReviewing && (
                  <div>
                    <ReviewStep
                      games={wizardScheduleResult.games}
                      provider={wizardScheduleResult.provider}
                      filename={wizardScheduleResult.filename}
                      totalCostCents={wizardScheduleResult.totalCostCents}
                      costCurrency={wizardScheduleResult.costCurrency}
                      onReset={() => {
                        setWizardReviewing(false);
                        setWizardScheduleStatus('idle');
                        setWizardScheduleFile(null);
                        setWizardScheduleResult(null);
                      }}
                      onConfirm={async (gamesPayload) => {
                        try {
                          const { data } = await api.post(
                            `/teams/${wizardTeam.id}/import-schedule/confirm`,
                            // 2026-07-10 (5.2) — pass the extracted permit
                            // total through so it's recorded to the budget.
                            {
                              games: gamesPayload,
                              totalCostCents: wizardScheduleResult.totalCostCents || undefined,
                              costCurrency: wizardScheduleResult.costCurrency || undefined,
                            }
                          );
                          toast.success(`🏟 ${data.created} game${data.created !== 1 ? 's' : ''} added!`);
                          if (data.costRecorded) toast.success('💰 Permit cost recorded in the team budget');
                          setWizardReviewing(false);
                          setWizardStep(4);
                        } catch (err) {
                          toast.error(err.response?.data?.error || 'Import failed. Please try again.');
                          throw err;
                        }
                      }}
                    />
                  </div>
                )}

                {wizardScheduleStatus === 'error' && (
                  <div className="space-y-4">
                    <div className="p-4 rounded-xl" style={{ background: 'rgba(255,68,68,0.08)', border: '1.5px solid rgba(255,68,68,0.4)' }}>
                      <p className="text-sm font-semibold mb-1" style={{ color: 'var(--penalty)' }}>Couldn't read that file</p>
                      <p className="text-xs text-secondary">{wizardScheduleError || 'Try a different file or skip and import later from the Schedule page.'}</p>
                    </div>
                    <div className="flex gap-3">
                      <button type="button" onClick={() => { setWizardScheduleStatus('idle'); setWizardScheduleFile(null); }} className="btn-ghost flex-1 justify-center">Try another file</button>
                      <button type="button" onClick={() => setWizardStep(4)} className="btn-ice flex-1 justify-center">Skip <ArrowRight size={14}/></button>
                    </div>
                  </div>
                )}
              </>
            )}

            {wizardStep === 4 && (
              <>
                <h2 className="section-title text-xl mb-1">Add Players</h2>
                <p className="text-secondary text-sm mb-5">
                  Set up your roster from the Players page after the wizard. If you already have another team
                  with the same group, you can copy that roster over now.
                </p>

                {/* Apr 2026 — wizard step 3 rebuilt. Removed the
                    one-at-a-time invite flow (coaches said it was
                    slow and they preferred the bulk Players page).
                    Replaced with a "copy from another team" picker
                    when applicable; otherwise just guidance to use
                    the Players page. */}
                {(() => {
                  // Only the user's *other* coach/manager teams qualify
                  // as copy sources — and not the team we just created.
                  const sourceTeams = (teams || []).filter(t =>
                    ['coach', 'manager'].includes(t.member_role)
                    && t.id !== wizardTeam?.id
                  );
                  if (sourceTeams.length > 0) {
                    return (
                      <div className="space-y-4">
                        <div>
                          <label className="label">Copy roster from</label>
                          <select
                            className="input-field"
                            value={wizardCopyFromTeamId}
                            onChange={e => setWizardCopyFromTeamId(e.target.value)}>
                            <option value="">— Don't copy, I'll add players later —</option>
                            {sourceTeams.map(t => (
                              <option key={t.id} value={t.id}>
                                {t.name}{t.league_name ? ` · ${t.league_name}` : ''} ({t.member_count || 0} {(t.member_count || 0) === 1 ? 'member' : 'members'})
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-secondary mt-1" style={{ lineHeight: 1.5 }}>
                            Copies players, ratings, positions, and aliases — pending invites and placeholders come along too.
                          </p>
                        </div>
                        {/* 2026-07-10 (Phase 1.5, coach request) — explicit
                            copy scope. */}
                        {wizardCopyFromTeamId && (
                          <div>
                            <label className="label">Who to copy</label>
                            <div className="grid grid-cols-3 gap-2">
                              {[
                                { key: 'everyone', label: 'Everyone' },
                                { key: 'fulltime', label: 'Full-time only' },
                                { key: 'spares',   label: 'Spares only' },
                              ].map(opt => (
                                <button key={opt.key} type="button"
                                  onClick={() => setWizardCopyScope(opt.key)}
                                  className="py-2 px-2 rounded-lg text-xs font-semibold"
                                  style={{
                                    background: wizardCopyScope === opt.key ? 'var(--ice-a12)' : 'var(--surface-2)',
                                    border: `1.5px solid ${wizardCopyScope === opt.key ? 'var(--ice-a30)' : 'var(--border)'}`,
                                    color: wizardCopyScope === opt.key ? 'var(--ice)' : 'var(--text-2)',
                                    cursor: 'pointer',
                                  }}>
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* 2026-08-07 — notify toggle (default ON). Existing
                            Game Day users copied onto this team get the
                            "you've been added" email; without it they were
                            added silently and never knew. */}
                        {wizardCopyFromTeamId && (
                          <label className="flex items-center gap-2 cursor-pointer text-sm"
                            style={{ color: 'var(--text-2)' }}>
                            <input
                              type="checkbox"
                              checked={wizardNotifyCopied}
                              onChange={e => setWizardNotifyCopied(e.target.checked)}
                              style={{ width: 16, height: 16, accentColor: 'var(--ice)' }}
                            />
                            <span>
                              Email copied players that they've been added
                              <span className="block text-xs" style={{ color: 'var(--text-3)' }}>
                                Players with Game Day accounts get a "you're on {`${wizardTeam?.name || 'the new team'}`}" email. Pending invites are emailed separately from the Players page.
                              </span>
                            </span>
                          </label>
                        )}
                        {/* 2026-07-10 (Phase 2.6, coach request) — bulk AI
                            import from step 3: same PDF/screenshot/CSV
                            workflow as the Players page, landing in the
                            importer for THIS new team. */}
                        <button type="button" onClick={wizardGoToImporter}
                          className="btn-ghost w-full justify-center">
                          📸 Or upload a roster — PDF, screenshot, or CSV
                        </button>
                        <div className="flex gap-3 pt-1">
                          <button type="button" onClick={() => setWizardStep(5)} className="btn-ghost flex-1 justify-center">Skip — I'll add later</button>
                          <button
                            type="button"
                            onClick={wizardCopyPlayers}
                            disabled={!wizardCopyFromTeamId || wizardCopyingPlayers}
                            className="btn-ice flex-1 justify-center">
                            {wizardCopyingPlayers ? <div className="spinner"/> : <><Copy size={14}/> Copy & Continue</>}
                          </button>
                        </div>
                      </div>
                    );
                  }
                  // No source teams — offer the bulk importer directly.
                  // 2026-07-10 (Phase 2.6) — was guidance-only; now the AI
                  // importer (PDF/screenshot/CSV) is one tap away.
                  return (
                    <div className="space-y-4">
                      <div className="p-4 rounded-xl"
                        style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.15)' }}>
                        <Users size={20} style={{ color: 'var(--ice)', marginBottom: 8 }} />
                        <p className="text-sm font-semibold mb-1">Bring your roster in one shot</p>
                        <p className="text-xs text-secondary" style={{ lineHeight: 1.5 }}>
                          Upload a PDF, screenshot, or CSV of your player list — the AI extracts names and
                          emails for your review, and everyone lands on this team and its season automatically.
                        </p>
                      </div>
                      <button type="button" onClick={wizardGoToImporter} className="btn-ice w-full justify-center">
                        📸 Upload a roster — PDF, screenshot, or CSV
                      </button>
                      <div className="flex gap-3 pt-1">
                        <button type="button" onClick={() => setWizardStep(5)} className="btn-ghost w-full justify-center">
                          Skip — I'll add players later <ArrowRight size={14}/>
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </>
            )}

            {wizardStep === 5 && (
              <div>
                {/* Apr 2026 — step 4 rebuilt as a coach-facing checklist
                    + menu summary. Replaces the simple "Schedule First
                    Game / Manage Players" CTAs. The new layout shows:
                      • A celebratory header
                      • A 3-item checklist for what to do next
                      • A short "menu glossary" so coaches know what
                        each sidebar item does
                      • CTA buttons that jump straight to the highest-
                        value next step (Seasons / Players)
                    Designed to be scannable on a phone — each item is
                    a single sentence with an emoji. */}
                <div className="text-center mb-5">
                  <div className="text-5xl mb-3" style={{ filter:'drop-shadow(0 0 20px rgba(0,255,136,0.5))' }}>🏟</div>
                  <h2 className="section-title text-xl mb-1">{wizardTeam?.name} is live!</h2>
                  <p className="text-secondary text-sm">A few things to do next.</p>
                </div>

                {/* Checklist */}
                <div className="mb-5">
                  <p className="text-xs font-bold mb-2" style={{
                    fontFamily: 'var(--font-display)', letterSpacing: '0.12em',
                    textTransform: 'uppercase', color: 'var(--ice)',
                  }}>
                    Coach checklist
                  </p>
                  <div className="space-y-2">
                    {/* 2026-07-10 (wizard v2, coach request) — checklist now
                        REFLECTS what actually happened in this wizard run:
                        the season is created in step 1 (✓ always), schedule
                        and players show ✓ or ✗ based on whether those steps
                        ran, with pointers to where skipped work lives. */}
                    {[
                      { num: 1, done: true,
                        title: `Season created${wizardTeam?.initialSeason?.name ? ` — ${wizardTeam.initialSeason.name}` : ''}`,
                        desc: 'Games and stats are scoped to it from day one. Add more seasons anytime under Seasons.' },
                      { num: 2, done: !!wizardScheduleResult,
                        title: wizardScheduleResult ? 'Schedule imported' : 'Schedule not imported',
                        desc: wizardScheduleResult
                          ? 'Review and fine-tune your games on the Schedule page.'
                          : 'Skipped — import anytime from the Schedule page (PDF or screenshot works).' },
                      { num: 3, done: wizardPlayersImported,
                        title: wizardPlayersImported ? 'Players added' : 'Players not added yet',
                        desc: wizardPlayersImported
                          ? 'Roster copied — review ratings and positions on the Players page.'
                          : 'Skipped — add or import players anytime from the Players section.' },
                      // 2026-07-11 (batch-B G2 follow-up) — nav sub-page was relabeled 'Payouts' → '🏦 Stripe' in teamSettingsSubNav.js; this onboarding copy still named the old label.
                      { num: 4, done: null, title: 'Connect Stripe (optional)', desc: 'Only if you plan to charge spares or season fees. Find it under Team Settings → Stripe.' },
                    ].map(item => (
                      <div key={item.num} className="flex items-start gap-3 p-3 rounded-xl"
                        style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                        <div style={{
                          width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                          background: item.done === true ? 'rgba(0,255,136,0.12)'
                                    : item.done === false ? 'rgba(255,149,0,0.12)'
                                    : 'var(--ice-a12)',
                          border: `1px solid ${item.done === true ? 'rgba(0,255,136,0.35)'
                                    : item.done === false ? 'rgba(255,149,0,0.35)'
                                    : 'var(--ice-a30)'}`,
                          color: item.done === true ? 'var(--goal, #00c878)'
                               : item.done === false ? 'var(--puck, #ff9500)'
                               : 'var(--ice)',
                          fontWeight: 700, fontSize: 12,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          marginTop: 1,
                        }}>
                          {item.done === true ? '✓' : item.done === false ? '✗' : item.num}
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <p className="text-sm font-semibold">{item.title}</p>
                          <p className="text-xs text-secondary mt-0.5" style={{ lineHeight: 1.4 }}>
                            {item.desc}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Menu glossary — a one-line summary for each sidebar item
                    so coaches don't have to discover them by clicking. */}
                <div className="mb-5">
                  <p className="text-xs font-bold mb-2" style={{
                    fontFamily: 'var(--font-display)', letterSpacing: '0.12em',
                    textTransform: 'uppercase', color: 'var(--ice)',
                  }}>
                    What's in the menu
                  </p>
                  <div className="space-y-1.5">
                    {[
                      { Icon: Calendar,       label: 'Schedule',       desc: 'View, create, and import games. RSVPs and balanced teams live here.' },
                      { Icon: Users,          label: 'Players',        desc: 'Manage full-timers, spares, ratings, and contact info.' },
                      { Icon: MessageSquare,  label: 'Chat',           desc: 'Team chat. Coaches can send "📣 Announcements" that always email everyone.' },
                      { Icon: Layers,         label: 'Seasons',        desc: 'Group games + roster into seasons (Fall, Winter, etc.). Stats are scoped per season.' },
                      { Icon: BarChart2,      label: 'Stats',          desc: 'Per-game and season-wide attendance + scoring stats.' },
                      { Icon: CreditCard,     label: 'Finance',        desc: 'Payments coming in, who owes what, and Stripe payout history.' },
                      { Icon: Settings,       label: 'Team Settings',  desc: 'All the knobs — reminders, spares, pricing, payouts, balancing, privacy.' },
                    ].map(item => (
                      <div key={item.label} className="flex items-start gap-2.5 py-1.5">
                        <item.Icon size={14} style={{ color: 'var(--ice)', flexShrink: 0, marginTop: 3 }} />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <p className="text-sm">
                            <span className="font-semibold">{item.label}</span>
                            <span className="text-secondary"> — {item.desc}</span>
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Primary CTAs — biased toward "Create a Season" since
                    that's checklist item 1 and the natural next step.
                    Apr 2026 — also call setActiveTeam(wizardTeam) so
                    the sidebar reflects the freshly-created team
                    instead of staying on whichever team was active
                    before the wizard opened. Skipping this would mean
                    a coach who creates Team B from Team A's dashboard
                    sees "Team A" highlighted in the sidebar after
                    landing on Team B's Seasons page. */}
                {/* 2026-07-10 (wizard v2, coach request) — single CTA.
                    "Create a Season" is obsolete (the season is created in
                    step 1); "Manage Players"/"I'll explore on my own"
                    removed per Gilles. Finishing does a FULL PAGE LOAD into
                    the new team (finishWizard) — fixes the reported bug
                    where sidebar items like Players were missing until a
                    manual refresh. */}
                <div className="flex flex-col gap-2">
                  <button onClick={finishWizard} className="btn-ice w-full justify-center">
                    🏒 Create Team
                  </button>
                </div>
              </div>
            )}
            <div className="sm:hidden" aria-hidden style={{ height: 70 }} />
          </div>
        </div>
      )}
    </div>
  );

}

// ─── DrinksDashboardTile (Apr 2026) ──────────────────────────────────────────
// Lightweight per-team drinks status surface for the dashboard.
// Three states:
//   • Nobody assigned → CTA "I'll bring drinks" (volunteer mode) or info
//                       "coach hasn't assigned yet" (scheduled mode)
//   • You're assigned → CTA "Step down" + reassuring "you're on drinks"
//   • Someone else    → info-only "Covered by NAME"
// Hidden entirely when the team has drinks disabled.
//
// Why a dashboard tile in addition to the per-game DrinksPanel:
//   players otherwise had to navigate into a specific game to find
//   the volunteer button. For low-effort weekly tasks like drinks,
//   discoverability matters — the tile sits near the top of the
//   dashboard so a player sees "drinks needed" the moment they
//   open the app.
// May 4 2026 (v2.5.68) — exported so GamePage can reuse it. Coach
// feedback: "On the game page, move drinks to the upcoming reminders
// for this game section, have it in the upcoming reminder, put it at
// the bottom of the list, so that it shows the first two reminders,
// then they can expand, then show the drinks person just below that.
// Have drinks positioned on the specific game as it's position and
// shown currently on the dashboard." We export this exact component
// (rather than building a duplicate) so the visual treatment + data
// fetch + interaction model stays identical between Dashboard and
// GamePage. The clickthrough lands on the same #drinks-duty hash —
// even when called from GamePage, that just scrolls to the existing
// section without a route change.
export function DrinksDashboardTile({ teamId, game, userId }) {
  const [drinks, setDrinks] = useState(undefined); // undefined = loading
  const [teamSettings, setTeamSettings] = useState(null);
  const [acting, setActing] = useState(false);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    if (!teamId || !game?.id) return;
    try {
      const [tRes, dRes] = await Promise.all([
        api.get(`/teams/${teamId}`).catch(() => ({ data: null })),
        api.get(`/teams/${teamId}/drinks/${game.id}`).catch(() => ({ data: null })),
      ]);
      setTeamSettings(tRes.data?.settings || {});
      setDrinks(dRes.data); // null = no row yet, object = existing
    } catch {
      setTeamSettings({});
      setDrinks(null);
    }
  }, [teamId, game?.id]);
  useEffect(() => { refresh(); }, [refresh]);

  if (drinks === undefined || teamSettings === null) return null;
  if (!teamSettings.drinks_enabled) return null;
  // Apr 2026 — when a coach has explicitly dismissed drinks for
  // the next game (status='ignored'), suppress the dashboard tile
  // entirely. Showing "Nobody on drinks yet — Be the hero" would
  // be misleading because the coach already said "we don't need
  // drinks for this one". The per-game DrinksPanel still shows the
  // dismissed state when the user drills in.
  if (drinks?.status === 'ignored') return null;

  const hasAssigned = drinks?.userId && drinks?.status !== 'ignored';
  const isMe        = drinks?.userId === userId;
  const mode        = teamSettings.drinks_mode || 'volunteer';

  // Build a friendly date label (e.g. "Sunday, Nov 17").
  const gameLabel = (() => {
    try {
      const d = new Date(game.game_date.slice(0, 10) + 'T12:00:00');
      return d.toLocaleDateString('en-CA', { weekday: 'long', month: 'short', day: 'numeric' });
    } catch { return 'next game'; }
  })();

  const handleVolunteer = async () => {
    setActing(true);
    try {
      if (isMe) {
        // Self-removal — same path as the per-game panel. Backend
        // notifies the coach if it was a coach-assigned slot.
        const { data } = await api.delete(`/teams/${teamId}/drinks/${game.id}/volunteer`);
        if (data?.wasCoachAssigned) {
          toast.success('Removed — your coach has been notified to assign someone else');
        } else {
          toast.success('Removed from drinks duty');
        }
      } else {
        await api.post(`/teams/${teamId}/drinks/${game.id}/volunteer`);
        toast.success("🥤 You're on drinks!");
      }
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update drinks');
    }
    setActing(false);
  };

  // Visual treatment changes based on state — green when covered,
  // amber when nobody on drinks (gentle nudge to volunteer), neutral
  // otherwise.
  let bg, borderColor, accent;
  if (isMe) {
    bg = 'rgba(0,212,255,0.08)';
    borderColor = 'rgba(0,212,255,0.30)';
    accent = 'var(--ice)';
  } else if (hasAssigned) {
    bg = 'rgba(0,232,122,0.06)';
    borderColor = 'rgba(0,232,122,0.20)';
    accent = 'var(--goal)';
  } else {
    bg = 'rgba(255,149,0,0.06)';
    borderColor = 'rgba(255,149,0,0.20)';
    accent = 'var(--puck)';
  }

  return (
    <div
      onClick={() => navigate(`/game/${game.id}#drinks-duty`)}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(`/game/${game.id}#drinks-duty`);
        }
      }}
      className="card mb-4"
      style={{
        background: bg, borderColor, padding: '14px 16px',
        cursor: 'pointer',
        // May 4 2026 — coach feedback: "let me click anywhere in this
        // group to access the drinks instead of the arrow at the end."
        // Whole card is now clickable; chevron stays as a visual
        // affordance. Inner action button stops propagation so
        // Volunteer/Step-down doesn't navigate.
        transition: 'transform 0.08s ease, box-shadow 0.12s ease',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = ''; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 28, lineHeight: 1, flexShrink: 0 }}>🥤</div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 11, color: 'var(--text-2)', textTransform: 'uppercase',
                        letterSpacing: '0.08em', marginBottom: 2 }}>
            Drinks Duty · {gameLabel}
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>
            {isMe
              ? "You're on drinks! 🏆"
              : hasAssigned
                ? `Covered by ${drinks.name}`
                : (mode === 'scheduled'
                    ? 'Coach hasn\'t assigned anyone yet'
                    : 'Nobody on drinks yet')}
          </div>
          {!hasAssigned && mode === 'volunteer' && (
            <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>
              Be the hero — bring drinks for the team.
            </div>
          )}
        </div>
        {/* Action button — only show if there's a useful action.
            Volunteer mode + nobody assigned: "I'll bring drinks"
            Volunteer mode + I'm assigned: "Step down"
            Scheduled mode + I'm assigned: "Step down"
            Scheduled mode + nobody assigned: no action (coach picks)
            Anyone else assigned: no action — coach can change inside the game */}
        {(isMe || (mode === 'volunteer' && !hasAssigned)) && (
          <button
            onClick={(e) => { e.stopPropagation(); handleVolunteer(); }}
            disabled={acting}
            style={{
              flexShrink: 0,
              padding: '8px 14px',
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 700,
              background: isMe ? 'transparent' : accent,
              color: isMe ? accent : '#000',
              border: `1px solid ${accent}`,
              cursor: acting ? 'not-allowed' : 'pointer',
              opacity: acting ? 0.6 : 1,
            }}>
            {acting ? '…' : (isMe ? 'Step down' : "I'll bring drinks")}
          </button>
        )}
        {/* Always offer a way to drill into the game for full
            details — coaches in particular want the assign panel.
            Apr 30 2026 (App_Change_List #5) — was navigating to
            /team/:teamId/games/:gameId which doesn't exist as a
            route (router has /team/:teamId/games for the list and
            /game/:gameId for the detail page). The mismatch
            produced a 404 every time anyone tapped the chevron.
            Now routes to /game/:gameId, matching every other
            game-detail navigation in the dashboard. */}
        {/* May 4 2026 — chevron is now visual affordance only; the
            entire card is the click target. Was a separate button
            that stopPropagation'd; now redundant since the card does
            the navigation directly. ── */}
        <div
          aria-hidden
          style={{
            flexShrink: 0,
            padding: 6,
            color: 'var(--text-2)',
            display: 'flex', alignItems: 'center',
          }}>
          <ChevronRight size={18} />
        </div>
      </div>
    </div>
  );
}
